// Info trees for small bodies and spacecraft: the Earth-orbit spacecraft, the
// main-belt bodies, near-Earth asteroids, comets, the dwarf planets beyond
// Neptune and the deep-space probes (src/ui/matryoshka.js renders them; the
// schema is at the head of earth.js). Keyed by the id getBody() is asked for,
// getSceneObjectId() in main.js: the scene object's name, lowercased, so
// "halley's comet" and 'voyager 1' keep their apostrophe and space.
//
// Every fact carries its source in the tree's references node, and
// docs/body-info-sources/small-bodies.md is the audit record (claim, value,
// source). Facts that change (distances, crews, status) say the date they
// were read. tests/body-info-small-bodies.test.js checks the shape.

// Earth orbit and L2: the stations and the telescopes.
const EARTH_ORBIT = {
  'iss': {
    id: 'iss',
    label: 'International Space Station',
    type: 'Crewed space station',
    summary: 'A laboratory in low Earth orbit, lived in without a break since November 2000.',
    payload: [
      {
        type: 'description',
        text: 'The International Space Station is a crewed laboratory in low Earth orbit, operated by five space agencies: NASA, Roscosmos, ESA, JAXA and the Canadian Space Agency. Its first module, Zarya, launched on 20 November 1998, and people have lived aboard continuously since the Expedition 1 crew arrived on 2 November 2000. It circles Earth about every 90 minutes, 16 times a day, and NASA describes it as the largest humanmade object ever to orbit Earth.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Size',              value: '109 m end to end' },
          { label: 'Mass',              value: '419,725 kg' },
          { label: 'Pressurised volume', value: '1,005 m³ (388 m³ habitable)' },
          { label: 'Orbit',             value: '370 to 460 km up, inclined 51.6°' },
          { label: 'Orbital period',    value: 'About 90 minutes (16 orbits a day)' },
          { label: 'First module',      value: 'Zarya, launched 20 November 1998' },
          { label: 'Operators',         value: 'NASA, Roscosmos, ESA, JAXA, CSA' },
          { label: 'Crew',              value: 'Seven aboard (as of 7 October 2026)' }
        ]
      }
    ],
    children: [
      {
        id: 'modules',
        label: 'Modules and assembly',
        summary: 'Built in orbit piece by piece from 1998',
        payload: [
          {
            type: 'description',
            text: 'The station was launched in pieces and joined together in orbit, using robotic arms and spacewalking crews to connect fluid lines and electrical wiring. NASA counts 43 modules and elements installed. Three nodes, Unity, Harmony and Tranquility, join the modules together, and eight main solar arrays provide 75 to 90 kilowatts of power.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Zarya',      secondary: 'Launched 20 November 1998. The first component, built and launched by Russia and funded by the US' },
              { primary: 'Unity',      secondary: 'Launched 4 December 1998, installed 6 December 1998' },
              { primary: 'Zvezda',     secondary: 'Launched 12 July 2000. Living quarters, life support and propulsion' },
              { primary: 'Destiny',    secondary: 'Launched 7 February 2001. The main US laboratory' },
              { primary: 'Canadarm2',  secondary: 'Launched 19 April 2001. A 17.6 m robotic arm that captures cargo craft and moves spacewalkers' },
              { primary: 'Columbus',   secondary: 'Launched 7 February 2008. ESA\'s laboratory' },
              { primary: 'Kibo',       secondary: 'Launched 31 May 2008. JAXA\'s laboratory, "hope" in Japanese' },
              { primary: 'Cupola',     secondary: 'Launched 8 February 2010. Seven windows for watching Earth, robotics and arriving craft' },
              { primary: 'Nauka',      secondary: 'Launched 21 July 2021. Russian science module' },
              { primary: 'Prichal',    secondary: 'Launched 24 November 2021, installed 26 November 2021. The most recent module, a Russian docking hub with five ports' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Assembly took more spacewalks than all earlier programmes combined. The first, on 7 December 1998, connected cables between Zarya and Unity; by November 2025 NASA counted more than 260.'
          }
        ]
      },
      {
        id: 'crew',
        label: 'Crew and life aboard',
        summary: 'Expedition 75, seven residents as of October 2026',
        payload: [
          {
            type: 'description',
            text: 'A crew of seven usually lives aboard, with more during a handover. The living space is larger than a six-bedroom house, with six sleeping quarters, two bathrooms, a gym and a 360-degree bay window. Crew members exercise at least two hours a day to limit the loss of muscle and bone in microgravity. By November 2025 the station had hosted more than 290 people from 26 countries.'
          },
          {
            type: 'stats',
            label: 'Expedition 75 (as of 7 October 2026)',
            entries: [
              { label: 'Began',              value: '26 July 2026, ends spring 2027' },
              { label: 'Pyotr Dubrov',       value: 'Station commander, Roscosmos' },
              { label: 'Anna Kikina',        value: 'Flight engineer, Roscosmos' },
              { label: 'Anil Menon',         value: 'Flight engineer, NASA' },
              { label: 'Jessica Watkins',    value: 'Flight engineer, NASA (Crew-13)' },
              { label: 'Luke Delaney',       value: 'Flight engineer, NASA (Crew-13)' },
              { label: 'Joshua Kutryk',      value: 'Flight engineer, CSA (Crew-13)' },
              { label: 'Sergey Teteryatnikov', value: 'Flight engineer, Roscosmos (Crew-13)' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Dubrov, Kikina and Menon began their mission on 14 July 2026, and the four Crew-13 members arrived on 1 October 2026. Crew-12 undocked on 7 October 2026 and splashed down off Los Angeles the next day after 237 days in space.'
          }
        ]
      },
      {
        id: 'research',
        label: 'Research',
        summary: 'A microgravity laboratory with thousands of experiments',
        payload: [
          {
            type: 'description',
            text: 'Crews carry out experiments for researchers on the ground in Earth and space science, biology, human physiology, physical sciences and technology demonstrations. By June 2024 more than 3,300 experiments had been conducted aboard. More than 20 research payloads can be mounted outside at once, among them the Alpha Magnetic Spectrometer, which has collected billions of cosmic ray events and identified 9 million of them as electrons or positrons.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Earth photography', secondary: 'The orbit passes over more than 90% of Earth\'s population, and crews have taken more than 3.5 million photographs of the planet' },
              { primary: 'Water recovery',    secondary: 'Recycling cuts the crew\'s need for delivered water by 65%' },
              { primary: 'Software',          secondary: 'About 350,000 sensors are monitored by on-orbit software; more than 50 computers control the station\'s systems' }
            ]
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'About 418 km up on 9 October 2026',
        payload: [
          {
            type: 'description',
            text: 'The station orbits between about 370 and 460 km up, inclined 51.6° to the equator. Atmospheric drag pulls it down continually, so it fires rockets from time to time to raise its orbit. On 9 October 2026, when Northrop Grumman\'s Cygnus XL cargo craft was released by Canadarm2, the station was flying about 418 km (260 miles) above the Pacific Ocean.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Altitude',  value: 'About 418 km (as of 9 October 2026)' },
              { label: 'Speed',     value: 'About 8 km/s (5 miles per second)' },
              { label: 'Ports',     value: 'Up to eight spacecraft docked at once' },
              { label: 'Visitors',  value: 'Dragon, Cygnus, Soyuz, Progress and others' }
            ]
          }
        ]
      },
      {
        id: 'end-of-life',
        label: 'The end of the station',
        summary: 'Operations to 2030, then a controlled deorbit',
        payload: [
          {
            type: 'description',
            text: 'The United States, Japan, Canada and the participating countries of ESA have committed to operating the station through 2030, and Russia through at least 2028. On 26 June 2024 NASA selected SpaceX to build the US Deorbit Vehicle, which will steer the station into a controlled re-entry away from populated areas. The contract has a total potential value of $843 million, and the vehicle is expected to break up along with the station on re-entry.'
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Space station facts and figures', url: 'https://www.nasa.gov/international-space-station/space-station-facts-and-figures/' },
              { label: 'NASA: International Space Station reference', url: 'https://www.nasa.gov/reference/international-space-station/' },
              { label: 'NASA: Expedition 75', url: 'https://www.nasa.gov/mission/expedition-75/' },
              { label: 'NASA: Station assembly elements', url: 'https://www.nasa.gov/international-space-station/international-space-station-assembly-elements/' },
              { label: 'NASA space station blog, 6 October 2026', url: 'https://www.nasa.gov/blogs/spacestation/2026/10/06/crew-12-ready-to-depart-as-new-residents-begin-advanced-health-research/' },
              { label: 'NASA space station blog', url: 'https://www.nasa.gov/blogs/spacestation/' },
              { label: 'NASA: US Deorbit Vehicle selected', url: 'https://www.nasa.gov/news-release/nasa-selects-international-space-station-us-deorbit-vehicle/' }
            ]
          }
        ]
      }
    ]
  },

  'hubble': {
    id: 'hubble',
    label: 'Hubble Space Telescope',
    type: 'Space telescope (NASA and ESA)',
    summary: 'A 2.4 m telescope in low Earth orbit since 1990, repaired and upgraded by astronauts five times.',
    payload: [
      {
        type: 'description',
        text: 'The Hubble Space Telescope is a 2.4 metre reflecting telescope in low Earth orbit, a partnership between NASA and the European Space Agency. The Space Shuttle Discovery carried it up on 24 April 1990, and astronauts visited it five times between 1993 and 2009 to repair it and install new instruments. It sees ultraviolet, visible and near-infrared light, and its distance measurements helped show that the expansion of the universe is speeding up.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Length',          value: '13.2 m (4.2 m wide)' },
          { label: 'Mass',            value: 'About 12,200 kg' },
          { label: 'Primary mirror',  value: '2.4 m' },
          { label: 'Orbit',           value: 'About 470 km up (October 2026), inclined 28.5°' },
          { label: 'Orbital period',  value: 'About 94 minutes' },
          { label: 'Launch',          value: '24 April 1990, Space Shuttle Discovery (STS-31)' },
          { label: 'Operators',       value: 'NASA with ESA' },
          { label: 'Status',          value: 'Active; 200,000th orbit on 19 September 2026' }
        ]
      }
    ],
    children: [
      {
        id: 'design',
        label: 'The telescope',
        summary: 'A Cassegrain reflector built to be serviced in orbit',
        payload: [
          {
            type: 'description',
            text: 'Hubble is a Cassegrain reflector: light from the 2.4 m primary mirror bounces off a 0.3 m secondary mirror and back through a hole in the primary into the instruments. Above the blurring of the atmosphere it can separate details 0.05 arcseconds apart, about 10 times finer than large ground-based telescopes typically manage, and detect objects 10 billion times fainter than the unaided eye can see. It was the first space observatory designed to be serviced by astronauts in orbit.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Pointing stability',  value: 'Varies less than 7 milliarcseconds over 24 hours' },
              { label: 'Solar power',         value: 'More than 5,200 W from two arrays, six batteries' },
              { label: 'Science data',        value: 'About 150 gigabits a week' },
              { label: 'Communications',      value: "Through NASA's Tracking and Data Relay Satellites" }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Soon after launch the primary mirror turned out to be flawed: its edge was too flat by about a fiftieth of the width of a human hair, which blurred the early images. NASA and ESA built corrective optics that astronauts fitted on the first servicing mission in December 1993.'
          }
        ]
      },
      {
        id: 'instruments',
        label: 'Instruments',
        summary: 'Cameras, spectrographs and fine guidance sensors',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'Wide Field Camera 3 (WFC3)',          secondary: 'Installed 2009. The main imager, in ultraviolet, visible and infrared light' },
              { primary: 'Advanced Camera for Surveys (ACS)',   secondary: 'Installed 2002, repaired 2009. Wide-field imaging, mainly in visible light' },
              { primary: 'Cosmic Origins Spectrograph (COS)',   secondary: 'Installed 2009. Ultraviolet spectroscopy of faint points of light such as quasars' },
              { primary: 'Space Telescope Imaging Spectrograph (STIS)', secondary: 'Repaired 2009. A versatile spectrograph that handles bright objects well' },
              { primary: 'Fine Guidance Sensors (three)',       secondary: 'Lock onto guide stars and measure star positions' },
              { primary: 'NICMOS',                              secondary: 'Near-infrared camera and spectrometer, inactive; WFC3 has taken over most of its work' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Only one instrument aboard today, the third Fine Guidance Sensor, flew up with the telescope in 1990. All the others were installed on servicing missions.'
          }
        ]
      },
      {
        id: 'servicing',
        label: 'Servicing missions',
        summary: 'Five shuttle crews visited between 1993 and 2009',
        payload: [
          {
            type: 'stats',
            entries: [
              { label: 'Deployment',  value: '24 to 29 April 1990, Discovery; released 25 April' },
              { label: 'SM1',         value: '2 to 13 December 1993, corrective optics fitted' },
              { label: 'SM2',         value: '11 to 21 February 1997, two new instruments' },
              { label: 'SM3A',        value: '19 to 27 December 1999, after a fourth gyroscope failed' },
              { label: 'SM3B',        value: '1 to 12 March 2002, new solar panels and ACS' },
              { label: 'SM4',         value: '11 to 24 May 2009, WFC3 and COS, ACS and STIS repaired' }
            ]
          },
          {
            type: 'description',
            text: 'Thirty-two astronauts flew on the Hubble shuttle missions, spending 171 hours and 3 minutes on spacewalks over 23 days. John Grunsfeld flew the most missions to Hubble (three) and made the most spacewalks there (eight). Servicing Mission 4, flown by Atlantis, also replaced all six gyroscopes and the batteries.'
          }
        ]
      },
      {
        id: 'discoveries',
        label: 'Key discoveries',
        summary: 'The expansion rate, dark energy, the deep fields',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'The expansion rate', secondary: 'Cepheid stars and supernovae measured with Hubble pin the Hubble Constant to just over 1%, putting the age of the universe at about 13.8 billion years' },
              { primary: 'An accelerating universe', secondary: 'Hubble and ground-based observatories found that the expansion is speeding up, a discovery that won the 2011 Nobel Prize in Physics' },
              { primary: 'The deep fields', secondary: 'The Hubble Deep Field, taken over 10 days in 1995, held roughly 3,000 galaxies; the Ultra Deep Field holds an estimated 10,000' },
              { primary: 'Exoplanet atmospheres', secondary: 'Hubble made the first measurements of a planet\'s atmosphere around another star, HD 209458, finding sodium, carbon and oxygen' },
              { primary: 'Black holes', secondary: 'Hubble showed that supermassive black holes sit at the cores of almost all galaxies' },
              { primary: 'The most distant star', secondary: 'Earendel, whose light travelled 12.9 billion years to reach Hubble' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'More than 23,000 peer-reviewed papers use Hubble data, cited more than 1.4 million times through 2024.'
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'Still observing after 36 years',
        payload: [
          {
            type: 'description',
            text: 'Hubble completed its 200,000th orbit of Earth on 19 September 2026, having travelled more than 8 billion km (5 billion miles) and made more than 1.7 million observations. Demand stays high: astronomers ask for about seven times as much observing time as there is each year. It now points using one gyroscope, keeping two working ones in reserve, and NASA engineers expect it to keep operating through the late 2020s and possibly into the 2030s.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Orbits',        value: '200,000 (19 September 2026)' },
              { label: 'Observations',  value: 'More than 1.7 million (September 2026)' },
              { label: 'Gyroscopes',    value: 'Three working, one in use (NASA, November 2024)' },
              { label: 'Speed',         value: 'About 27,000 km/h' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Hubble by the numbers', url: 'https://science.nasa.gov/mission/hubble/overview/about-hubble/hubble-by-the-numbers/' },
              { label: 'ESA: Hubble overview', url: 'https://www.esa.int/Science_Exploration/Space_Science/Hubble_overview' },
              { label: 'NASA: Astronaut missions to Hubble', url: 'https://science.nasa.gov/mission/hubble/observatory/missions-to-hubble/' },
              { label: 'NASA: Hubble mission', url: 'https://science.nasa.gov/mission/hubble/' },
              { label: 'NASA: Hubble design', url: 'https://science.nasa.gov/mission/hubble/observatory/design/' },
              { label: 'NASA: Hubble instruments', url: 'https://science.nasa.gov/mission/hubble/observatory/design/instruments/' },
              { label: 'NASA: Hubble science highlights', url: 'https://science.nasa.gov/mission/hubble/science/science-highlights/' },
              { label: 'NASA: Discovering the runaway universe', url: 'https://science.nasa.gov/mission/hubble/science/science-highlights/discovering-a-runaway-universe/' },
              { label: 'NASA: Tracing the growth of galaxies', url: 'https://science.nasa.gov/mission/hubble/science/science-highlights/tracing-the-growth-of-galaxies/' },
              { label: 'NASA: Hubble marks 200,000 orbits (23 September 2026)', url: 'https://science.nasa.gov/missions/hubble/nasas-hubble-seeks-lensed-supernova-marks-200000-orbits/' },
              { label: 'CelesTrak: Hubble orbital elements (NORAD 20580, 30 September 2026)', url: 'https://celestrak.org/NORAD/elements/gp.php?CATNR=20580' }
            ]
          }
        ]
      }
    ]
  },

  'tiangong': {
    id: 'tiangong',
    label: 'Tiangong space station',
    type: 'Crewed space station (China)',
    summary: "China's three-module space station, complete in its T shape since November 2022.",
    payload: [
      {
        type: 'description',
        text: 'Tiangong, Chinese for "heavenly palace", is the space station of the China Manned Space Agency. Its core module, Tianhe, launched on 29 April 2021, and the laboratory modules Wentian and Mengtian followed in 2022, completing the station\'s basic T-shaped structure on 3 November 2022. Crews arrive on Shenzhou spacecraft and hand over to the next crew in orbit, while Tianzhou cargo craft bring supplies.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Modules',         value: '3 (Tianhe, Wentian, Mengtian)' },
          { label: 'Module mass',     value: 'About 68.5 t combined at launch' },
          { label: 'Orbit',           value: 'About 384 to 399 km up, inclined 41.4° (as of 9 October 2026)' },
          { label: 'Orbital period',  value: 'About 92.3 minutes (as of 9 October 2026)' },
          { label: 'First module',    value: 'Tianhe, launched 29 April 2021' },
          { label: 'Operator',        value: 'China Manned Space Agency (CMSA)' },
          { label: 'Design life',     value: '10 years' },
          { label: 'Crew',            value: 'Three, Shenzhou-23 (as of 9 September 2026)' }
        ]
      }
    ],
    children: [
      {
        id: 'modules',
        label: 'Modules',
        summary: 'A core module and two laboratories',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'Tianhe (core module)', secondary: '16.6 m long, 4.2 m across, 22.5 t at launch, the largest spacecraft China had built. The station\'s command centre and main living quarters, with about 50 m³ of habitable space, two berthing ports, three docking ports and a spacewalk exit' },
              { primary: 'Wentian (laboratory)', secondary: '17.9 m long, 4.2 m across, about 23 t. A working module, airlock and resource module; it can back up Tianhe in running the station, and spacewalks start from its airlock' },
              { primary: 'Mengtian (laboratory)', secondary: 'About 23 t. Working, payload, cargo airlock and resource modules, used mainly for microgravity science; its cargo airlock passes experiments outside' }
            ]
          },
          {
            type: 'description',
            text: "Wentian's flexible solar wings span more than 55 m when deployed, each about 110 m², and were expected to generate over 430 kWh a day on average. With the two laboratories in place, Zhou Jianping, chief designer of China's crewed space programme, expected a total living space of 110 m³."
          }
        ]
      },
      {
        id: 'assembly',
        label: 'Building the station',
        summary: 'The third step of a programme approved in 1992',
        payload: [
          {
            type: 'description',
            text: "China approved its crewed space programme in 1992 with a three-step strategy: crewed spacecraft first, then spacewalks, docking and space laboratories (Tiangong-1 and Tiangong-2), then a long-term space station. Building Tiangong is the third step."
          },
          {
            type: 'stats',
            label: 'Timeline (Beijing time)',
            entries: [
              { label: '29 April 2021',     value: 'Tianhe launched on a Long March-5B from Wenchang, 11:23' },
              { label: '24 July 2022',      value: 'Wentian launched, 14:22; docked to Tianhe at 03:13 on 25 July' },
              { label: '30 September 2022', value: 'Wentian moved to a side port, making an L shape' },
              { label: '31 October 2022',   value: 'Mengtian launched, 15:37; docked at 04:27 on 1 November' },
              { label: '3 November 2022',   value: 'Mengtian moved to a side port at 09:32, completing the T shape' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Each laboratory first docked at the front of Tianhe and was then swung round to a side port of the node cabin, a manoeuvre that took about an hour each time.'
          }
        ]
      },
      {
        id: 'crew',
        label: 'Crew and research',
        summary: 'Shenzhou-23 is aboard for a planned year',
        payload: [
          {
            type: 'description',
            text: 'The Shenzhou-23 crew launched from Jiuquan on a Long March-2F at 23:08 Beijing time on 24 May 2026 and took over from the Shenzhou-21 crew, who had spent 204 days in orbit by then. The crew is set to carry out a one-year in-orbit stay experiment and plans more than 100 new science and application projects in space life science, materials, fluid physics and medicine. On 9 September 2026 the three gave a live lesson from the station to students in Hong Kong and Macao.'
          },
          {
            type: 'stats',
            label: 'Shenzhou-23 (as of 9 September 2026)',
            entries: [
              { label: 'Zhu Yangzhu',   value: 'Commander and flight engineer; flew on Shenzhou-16' },
              { label: 'Zhang Zhiyuan', value: 'Spacecraft pilot, first flight' },
              { label: 'Li Jiaying',    value: 'Payload specialist, first astronaut from Hong Kong' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'By the Shenzhou-23 launch China had sent 30 astronauts into space. Li Jiaying, Lai Ka-ying in Cantonese, is the fourth Chinese woman to reach space.'
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'About 390 km up on 9 October 2026',
        payload: [
          {
            type: 'description',
            text: "CMSA publishes the station's predicted orbit as a table of positions. The table issued on 9 October 2026 puts Tiangong between about 384 and 399 km above Earth's surface, on an orbit inclined 41.4° to the equator that takes about 92.3 minutes, or roughly 15.6 orbits a day. The station was designed to operate for 10 years, and experts quoted by CGTN at the Tianhe launch thought it could last more than 15 with maintenance."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Altitude',     value: 'About 384 to 399 km (9 October 2026)' },
              { label: 'Inclination',  value: '41.4°' },
              { label: 'Period',       value: 'About 92.3 minutes' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'CMSA: Wentian lab module', url: 'https://en.cmse.gov.cn/missions/wentian/' },
              { label: 'CMSA: Mengtian lab module', url: 'https://en.cmse.gov.cn/missions/mengtian/' },
              { label: 'CGTN: Tianhe core module enters orbit (29 April 2021)', url: 'https://news.cgtn.com/news/2021-04-29/Rocket-carrying-China-s-space-station-core-module-blasts-off-ZQL7WBUE8w/index.html' },
              { label: 'CMSA: Orbital parameters of the China Space Station', url: 'https://en.cmse.gov.cn/news/202107/t20210722_48418.html' },
              { label: 'CMSA: Missions', url: 'https://en.cmse.gov.cn/missions/' },
              { label: 'CMSA: About China Manned Space', url: 'https://en.cmse.gov.cn/aboutcms/' },
              { label: 'CGTN: China launches Tianhe', url: 'https://news.cgtn.com/news/2021-04-29/China-launches-Tianhe-core-module-of-space-station-ZQOcOlv0li/index.html' },
              { label: 'Xinhua: China launches first lab module, Wentian', url: 'https://english.news.cn/20220725/c19f043908fa4008a08086d85badf552/c.html' },
              { label: 'gov.cn: Mengtian docks with the station', url: 'https://english.www.gov.cn/news/topnews/202211/01/content_WS6360709fc6d0a757729e1fe5.html' },
              { label: 'CGTN: Mengtian transposition completed', url: 'https://news.cgtn.com/news/2022-11-03/China-completes-transposition-of-Mengtian-lab-module-1eEuRYpdagg/index.html' },
              { label: 'Xinhua: China launches Shenzhou-23 (24 May 2026)', url: 'https://english.news.cn/20260525/e71e193dd1d14e1193edc9173f3f0477/c.html' },
              { label: 'Xinhua: Tiangong Class with the Shenzhou-23 crew (9 September 2026)', url: 'https://english.news.cn/20260909/5e8100bba2d3488bb673d8754c3573fa/c.html' }
            ]
          }
        ]
      }
    ]
  },

  'jwst': {
    id: 'jwst',
    label: 'James Webb Space Telescope',
    type: 'Infrared space observatory (NASA, ESA, CSA)',
    summary: 'A 6.5 m infrared telescope orbiting the Sun-Earth L2 point, 1.5 million km from Earth.',
    payload: [
      {
        type: 'description',
        text: 'The James Webb Space Telescope is an infrared observatory built by NASA with the European and Canadian space agencies. It launched on an Ariane 5 from Kourou, French Guiana, on 25 December 2021 and reached the second Sun-Earth Lagrange point, L2, about 1.5 million km from Earth, on 24 January 2022. A five-layer sunshield the size of a tennis court keeps its 6.5 metre gold-coated mirror and instruments below 50 K, so that the telescope\'s own heat does not drown out the faint infrared light it collects.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Primary mirror',  value: '6.5 m, 18 gold-coated beryllium segments' },
          { label: 'Sunshield',       value: '21.197 m × 14.162 m, five layers' },
          { label: 'Mass at launch',  value: 'About 6,200 kg, with propellant and launch adaptor' },
          { label: 'Orbit',           value: 'Around Sun-Earth L2, 1.5 million km from Earth' },
          { label: 'Wavelengths',     value: '0.6 to 28.5 microns' },
          { label: 'Launch',          value: '25 December 2021, 12:20 UTC, Ariane 5 ECA' },
          { label: 'Partners',        value: 'NASA, ESA, CSA' },
          { label: 'Status',          value: 'Active (NASA, as of 8 October 2026)' }
        ]
      }
    ],
    children: [
      {
        id: 'mission',
        label: 'The mission',
        summary: 'Four science themes, from first light to other worlds',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'Early universe',     secondary: 'Search for the first galaxies or luminous objects formed after the Big Bang' },
              { primary: 'Galaxies over time', secondary: 'Work out how galaxies evolved from their formation until now' },
              { primary: 'Star lifecycle',     secondary: 'Watch stars form, from their first stages to planetary systems' },
              { primary: 'Other worlds',       secondary: 'Measure planetary systems, our own included, and look into the potential for life' }
            ]
          },
          {
            type: 'description',
            text: 'Webb was designed for a mission of 5 to 10 years, and it is too far away for astronauts to service. The Ariane 5 launch was so precise that the first course corrections used less propellant than planned, and NASA said in December 2021 that Webb should have enough for significantly more than a 10-year science lifetime.'
          }
        ]
      },
      {
        id: 'instruments',
        label: 'Instruments',
        summary: 'Four infrared instruments behind the mirror',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'NIRCam',     secondary: 'Near-Infrared Camera, the primary camera; its coronagraph helps track planets round their stars' },
              { primary: 'NIRSpec',    secondary: 'Near-Infrared Spectrograph; its microshutters let it observe more than 100 objects at once' },
              { primary: 'MIRI',       secondary: 'Mid-Infrared Instrument, a camera and spectrograph whose detectors a cryocooler keeps below 7 K' },
              { primary: 'FGS/NIRISS', secondary: 'Fine Guidance Sensor, which points the telescope, packaged with a near-infrared imager and slitless spectrograph used for exoplanet atmospheres' }
            ]
          },
          {
            type: 'stats',
            entries: [
              { label: 'Focal length',    value: '131.4 m' },
              { label: 'Resolution',      value: 'About 0.1 arcseconds' },
              { label: 'Mirror area',     value: '25 m² clear aperture' },
              { label: 'Gold on mirror',  value: 'About 48 g, 100 nanometres thick' }
            ]
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'Looping round L2 on a six-month orbit',
        payload: [
          {
            type: 'description',
            text: 'Webb orbits the Sun about 1.5 million km beyond Earth, circling the L2 point on a loop roughly the size of the Moon\'s orbit that takes about six months. The Sun, Earth and Moon all stay on the same side, so the sunshield can block them, and Webb never passes into Earth\'s shadow. The Deep Space Network\'s antennas in Australia, Spain and California send commands and receive data up to twice a day.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Distance from Earth', value: 'About 1.5 million km (1.2 to 1.75 million km on its loop)' },
              { label: 'Arrived at L2',       value: '24 January 2022' },
              { label: 'Path drawn',          value: "JPL Horizons' (-170): flown to 2 November 2026, predicted to 1 November 2031" },
              { label: 'Status',              value: 'Active mission (NASA, 8 October 2026)' }
            ]
          }
        ]
      },
      {
        id: 'discoveries',
        label: 'Key discoveries',
        summary: 'Galaxies at cosmic dawn, exoplanet atmospheres',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'First images',          secondary: 'Released 11 and 12 July 2022, led by the deep field of galaxy cluster SMACS 0723' },
              { primary: 'The most distant galaxies', secondary: 'JADES-GS-z14-0 at redshift 14.32 in 2024, then MoM-z14 at 14.44, confirmed in June 2025, seen about 280 million years after the Big Bang' },
              { primary: 'Little red dots',       secondary: 'A population of compact red objects that appears about 600 million years after the Big Bang and declines by 1.5 billion years' },
              { primary: 'Carbon dioxide on WASP-39 b', secondary: 'Detected in the transmission spectrum of a hot gas giant' },
              { primary: 'An atmosphere on a rocky planet', secondary: 'The strongest evidence yet, around the ultra-hot super-Earth TOI-561 b' },
              { primary: 'A planet imaged directly', secondary: "Webb's first direct-imaging discovery, a planet of about Saturn's mass around the young star TWA 7" }
            ]
          }
        ]
      },
      {
        id: 'timeline',
        label: 'Timeline',
        summary: 'From a 1989 workshop to science operations',
        payload: [
          {
            type: 'stats',
            entries: [
              { label: '1989',              value: 'STScI and NASA hold the Next Generation Space Telescope workshop' },
              { label: '1996',              value: 'A committee led by Alan Dressler recommends an infrared space telescope' },
              { label: '2002',              value: 'Renamed the James Webb Space Telescope' },
              { label: '2004',              value: 'Construction of the instruments and mirror segments begins' },
              { label: '2011',              value: 'All 18 mirror segments finished and tested' },
              { label: '2017',              value: 'Cryogenic test in Chamber A at NASA Johnson' },
              { label: '25 December 2021',  value: 'Launch from Kourou' },
              { label: '24 January 2022',   value: 'Arrival at L2' },
              { label: '12 July 2022',      value: 'First full-colour images and spectra released' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Webb fact sheet', url: 'https://science.nasa.gov/mission/webb/fact-sheet/' },
              { label: 'NASA: Webb mission', url: 'https://science.nasa.gov/mission/webb/' },
              { label: "NASA: Webb's orbit", url: 'https://science.nasa.gov/mission/webb/orbit/' },
              { label: 'ESA: Webb', url: 'https://www.esa.int/Science_Exploration/Space_Science/Webb' },
              { label: "NASA: Webb's launch", url: 'https://science.nasa.gov/mission/webb/launch/' },
              { label: 'NASA: Webb mission timeline', url: 'https://science.nasa.gov/mission/webb/webb-mission-timeline/' },
              { label: "NASA: Webb's instruments (ISIM)", url: 'https://science.nasa.gov/mission/webb/integrated-science-instrument-module-isim/' },
              { label: 'NASA: Early universe', url: 'https://science.nasa.gov/mission/webb/early-universe/' },
              { label: 'NASA: Other worlds', url: 'https://science.nasa.gov/mission/webb/other-worlds/' },
              { label: "NASA: Webb's first images", url: 'https://science.nasa.gov/mission/webb/webbs-first-images/' },
              { label: "NASA: Webb's excess fuel (29 December 2021)", url: 'https://science.nasa.gov/blogs/webb/2021/12/29/nasa-says-webbs-excess-fuel-likely-to-extend-its-lifetime-expectations/' },
              { label: 'JPL Horizons: Webb (-170) ephemeris', url: 'https://ssd.jpl.nasa.gov/horizons/app.html#/?sstr=-170' }
            ]
          }
        ]
      }
    ]
  }
};

// The main asteroid belt.
const MAIN_BELT = {
  ceres: {
    id: 'ceres',
    label: 'Ceres',
    type: 'Dwarf planet (main asteroid belt)',
    summary: 'The largest object in the asteroid belt and the only dwarf planet in the inner solar system.',
    payload: [
      {
        type: 'description',
        text: "Ceres is the largest object in the asteroid belt between Mars and Jupiter and the only dwarf planet in the inner solar system. Giuseppe Piazzi found it from Palermo on 1 January 1801, the first member of the belt to be discovered, and the IAU named it one of the first dwarf planets in 2006. NASA's Dawn spacecraft orbited it from 2015 to 2018, making Ceres the first dwarf planet visited by a spacecraft. Dawn found bright salt deposits in Occator Crater fed by a deep reservoir of brine."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: '939.4 km (mean)' },
          { label: 'Mass',              value: '9.38 × 10²⁰ kg' },
          { label: 'Mean density',      value: '2.16 g/cm³' },
          { label: 'Distance from Sun', value: '2.77 AU (about 413 million km)' },
          { label: 'Orbital period',    value: '1,680 days (4.60 years)' },
          { label: 'Rotation period',   value: '9.074 hours' },
          { label: 'Moons',             value: 'None' },
          { label: 'Discovered',        value: '1 January 1801, Giuseppe Piazzi, Palermo' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Discovery and classification',
        summary: 'Asteroid number 1 for two centuries, a dwarf planet since 2006',
        payload: [
          {
            type: 'description',
            text: "Piazzi spotted Ceres from Palermo on 1 January 1801, and it is minor planet number 1. It is named for the Roman goddess of corn and harvests, and the word cereal comes from the same name. Called an asteroid for many years, Ceres was reclassified on 24 August 2006, when the IAU General Assembly in Prague defined a dwarf planet as a body that orbits the Sun, is massive enough to pull itself into a nearly round shape, has not cleared the neighbourhood around its orbit and is not a satellite."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Designation',      value: '1 Ceres (A801 AA)' },
              { label: 'Discovered',       value: '1 January 1801' },
              { label: 'Discoverer',       value: 'Giuseppe Piazzi, Palermo' },
              { label: 'Dwarf planet since', value: '24 August 2006 (IAU General Assembly, Prague)' },
              { label: 'Spectral class',   value: 'C (Bus), G (Tholen)' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The IAU press release of 24 August 2006 named the first members of the new dwarf planet category as Ceres, Pluto and 2003 UB313, the object now called Eris.'
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit and spin',
        summary: 'A nearly circular orbit in the middle of the belt, spinning almost upright',
        payload: [
          {
            type: 'description',
            text: "Ceres follows a nearly circular orbit 2.77 AU from the Sun, so sunlight takes 22 minutes to reach it. A day on Ceres lasts about 9 hours, one of the shortest in the solar system. Its axis is tilted only 4 degrees to its orbit, so it spins nearly upright and has no seasons like those of the more tilted planets."
          },
          {
            type: 'stats',
            label: 'Orbit (osculating, epoch 9 June 2026)',
            entries: [
              { label: 'Semi-major axis', value: '2.766 AU' },
              { label: 'Perihelion',      value: '2.545 AU' },
              { label: 'Aphelion',        value: '2.986 AU' },
              { label: 'Eccentricity',    value: '0.080' },
              { label: 'Inclination',     value: '10.59°' },
              { label: 'Orbital period',  value: '1,679.9 days' },
              { label: 'Rotation period', value: '9.074 hours' },
              { label: 'Axial tilt',      value: 'About 4°' }
            ]
          }
        ]
      },
      {
        id: 'surface',
        label: 'Occator Crater and the bright spots',
        summary: 'Sodium carbonate left behind by brine from deep inside',
        payload: [
          {
            type: 'description',
            text: "Ceres is dark overall, but its 92 km wide Occator Crater holds the most extensive bright areas, named Cerealia Facula and Vinalia Faculae. Dawn's infrared spectra showed they are mostly sodium carbonate, the most concentrated deposit of carbonate known beyond Earth on kilometre-wide scales, left behind when salty liquid reached the surface and evaporated. Gravity data from the end of the mission traced that liquid to a reservoir of brine about 40 km deep and hundreds of kilometres wide."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Occator Crater', secondary: '92 km wide, formed about 20 million years ago, home to the most extensive bright areas' },
              { primary: 'Cerealia Facula', secondary: 'The large bright deposit, where Dawn found salts that still hold water' },
              { primary: 'Vinalia Faculae', secondary: 'The second group of highly reflective deposits in the crater' },
              { primary: 'Pingo-like hills', secondary: 'Conical hills like the frozen groundwater mounds of polar Earth, the first seen on a dwarf planet' },
              { primary: 'Large craters', secondary: 'None larger than 280 km, a surprise for a body 4.5 billion years old' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Some of the bright deposits are less than 2 million years old, and the salts in Cerealia Facula still hold water, which they would lose within hundreds of years on the surface. The fluids must have arrived very recently, and the activity may be continuing.'
          }
        ]
      },
      {
        id: 'interior',
        label: 'Interior and water',
        summary: 'A rocky core under a shell rich in water ice and salts',
        payload: [
          {
            type: 'description',
            text: "Dawn's gravity and shape measurements showed that Ceres is in hydrostatic equilibrium and only partly differentiated, with a rocky core beneath a shell rich in volatiles. NASA estimates that Ceres could be as much as 25 percent water, which would give it more water than Earth. The crust is rocky and dusty with large salt deposits, and some shadowed craters may trap water ice for long periods."
          },
          {
            type: 'stats',
            label: 'Two-layer model (Park et al. 2016)',
            entries: [
              { label: 'Moment of inertia', value: '0.37 (normalised mean)' },
              { label: 'Core density',      value: '2,460 to 2,900 kg/m³ (assumed)' },
              { label: 'Outer shell',       value: '70 to 190 km thick' },
              { label: 'Shell density',     value: '1,680 to 1,950 kg/m³' },
              { label: 'Shape',             value: '964.4 × 964.2 × 891.8 km' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Ceres has a very thin atmosphere with evidence of water vapour, which may come from ice volcanoes or from ice near the surface turning straight to gas.'
          }
        ]
      },
      {
        id: 'dawn',
        label: 'The Dawn mission',
        summary: 'Orbited Vesta, then Ceres, on ion engines',
        payload: [
          {
            type: 'description',
            text: "Dawn launched from Cape Canaveral on 27 September 2007, orbited Vesta from 2011 to 2012 and entered orbit around Ceres on 6 March 2015. It is the only spacecraft to have orbited two extraterrestrial destinations, which its xenon ion engines made possible. In its final extended mission Dawn dropped to 35 km above the surface to study Occator Crater, until its hydrazine for pointing ran out. It remains in a long orbit chosen so that it will not strike Ceres for at least 20 years."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Launch',             value: '27 September 2007, Delta II, Cape Canaveral' },
              { label: 'Mars gravity assist', value: '18 February 2009' },
              { label: 'At Vesta',           value: '16 July 2011 to 5 September 2012' },
              { label: 'Ceres orbit entry',  value: '6 March 2015' },
              { label: 'Lowest altitude',    value: '35 km' },
              { label: 'Last contact',       value: '31 October 2018' },
              { label: 'Mission ended',      value: '1 November 2018' },
              { label: 'Operator',           value: 'NASA, managed by JPL' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Ceres facts', url: 'https://science.nasa.gov/dwarf-planets/ceres/facts/' },
              { label: 'JPL Small-Body Database: 1 Ceres', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=1' },
              { label: 'JPL: Bright areas on Ceres come from salty water below (2020)', url: 'https://www.jpl.nasa.gov/news/mystery-solved-bright-areas-on-ceres-come-from-salty-water-below/' },
              { label: 'NASA: Ceres overview', url: 'https://science.nasa.gov/dwarf-planets/ceres/' },
              { label: 'NASA: Dwarf planets', url: 'https://science.nasa.gov/dwarf-planets/' },
              { label: 'NASA: Dawn mission', url: 'https://science.nasa.gov/mission/dawn/' },
              { label: 'NASA: Dawn end of mission', url: 'https://science.nasa.gov/mission/dawn/toolkit/end-of-mission/' },
              { label: 'NSSDCA: Dawn spacecraft', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=2007-043A' },
              { label: 'NSSDCA: Asteroid fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/asteroidfact.html' },
              { label: 'IAU press release iau0603 (24 August 2006)', url: 'https://iauarchive.eso.org/news/pressreleases/detail/iau0603/' },
              { label: 'De Sanctis et al. 2016, Nature 536, 54 (sodium carbonate)', url: 'https://doi.org/10.1038/nature18290' },
              { label: 'Park et al. 2016, Nature 537, 515 (interior)', url: 'https://doi.org/10.1038/nature18955' },
              { label: 'Vernazza et al. 2021, A&A 654, A56 (sizes and masses)', url: 'https://doi.org/10.1051/0004-6361/202141781' }
            ]
          }
        ]
      }
    ]
  },

  vesta: {
    id: 'vesta',
    label: 'Vesta',
    type: 'Main-belt asteroid',
    summary: 'The second most massive body in the asteroid belt, the source of the HED meteorites.',
    payload: [
      {
        type: 'description',
        text: "Vesta is the second most massive body in the main asteroid belt, after Ceres, with almost 9 percent of the mass of all asteroids. Heinrich Wilhelm Olbers discovered it from Bremen on 29 March 1807, and NASA's Dawn spacecraft orbited it from July 2011 to September 2012. Its south pole is dominated by Rheasilvia, an impact basin about 500 km wide, almost as wide as Vesta itself. Vesta appears to be the source of the howardite, eucrite and diogenite meteorites found on Earth."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: '523 km mean (569 × 554 × 453 km)' },
          { label: 'Mass',              value: '2.59 × 10²⁰ kg' },
          { label: 'Mean density',      value: '3.45 g/cm³' },
          { label: 'Distance from Sun', value: '2.36 AU (semi-major axis)' },
          { label: 'Orbital period',    value: '1,325 days (3.63 years)' },
          { label: 'Rotation period',   value: '5.342 hours' },
          { label: 'Spectral class',    value: 'V' },
          { label: 'Discovered',        value: '29 March 1807, H. W. Olbers, Bremen' },
          { label: 'Shape',             value: 'Dawn' },
          { label: 'Surface shown',     value: 'Dawn Framing Camera mosaic (to 74° N)' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Discovery and name',
        summary: 'The fourth asteroid found, named by Gauss',
        payload: [
          {
            type: 'description',
            text: "Olbers, who had already discovered Pallas, found Vesta at Bremen on 29 March 1807 while searching for more fragments of what he believed was a destroyed planet, an idea that turned out to be wrong. It was the fourth asteroid ever discovered. Olbers gave the honour of naming it to the mathematician Carl Friedrich Gauss, who had computed its orbit, and Gauss chose Vesta, the Roman goddess of the hearth and household."
          },
          {
            type: 'stats',
            label: 'Orbit (osculating, epoch 9 June 2026)',
            entries: [
              { label: 'Designation',     value: '4 Vesta (A807 FA)' },
              { label: 'Semi-major axis', value: '2.361 AU' },
              { label: 'Perihelion',      value: '2.148 AU' },
              { label: 'Aphelion',        value: '2.574 AU' },
              { label: 'Eccentricity',    value: '0.090' },
              { label: 'Inclination',     value: '7.14°' },
              { label: 'Orbital period',  value: '1,325.4 days' }
            ]
          }
        ]
      },
      {
        id: 'surface',
        label: 'Rheasilvia and the surface',
        summary: 'Giant south-pole basins, equatorial troughs and a patchy bright and dark surface',
        payload: [
          {
            type: 'description',
            text: "Vesta is thought to have lost about 1 percent of its mass less than a billion years ago in the collision that made Rheasilvia, a crater about 500 km wide, some 95 percent of the asteroid's mean diameter. The Vesta family of asteroids is probably debris from that impact. Vesta has one of the largest ranges of brightness seen on any rocky body in the solar system: the bright material appears to be native rock, and the dark material was likely delivered by other asteroids crashing into it."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Rheasilvia', secondary: 'South-pole impact basin about 500 km wide, with a prominent central peak' },
              { primary: 'Veneneia', secondary: 'A second huge basin, about 400 km across' },
              { primary: 'Divalia Fossa', secondary: 'The largest of the troughs that circle the equator, bigger than the Grand Canyon' },
              { primary: 'Dark material', secondary: 'From an estimated 300 dark asteroids 1 to 10 km across that hit Vesta over the last 3.5 billion years' }
            ]
          }
        ]
      },
      {
        id: 'interior',
        label: 'Interior and the HED meteorites',
        summary: 'A melted protoplanet whose crust has fallen to Earth as meteorites',
        payload: [
          {
            type: 'description',
            text: "Vesta formed early, within 1 to 2 million years of the birth of the solar system, and short-lived radioactive material heated it until it melted and separated into layers, with denser material sinking and lighter material rising. Its crust appears to be the source of the howardite, eucrite and diogenite (HED) meteorites, which give clues to Vesta's geochemical history and to the early bombardment of the solar system."
          },
          {
            type: 'callout',
            tone: 'info',
            text: "A 2025 analysis of Dawn's tracking and imaging data (Park et al., Nature Astronomy) measured Vesta's moment of inertia at 0.4208, only 6.6 percent below that of a uniform body. That points to a small core and little layering beneath the HED crust. The authors propose that Vesta either never fully differentiated because it formed late, or re-formed from the debris of a larger differentiated body after a catastrophic impact."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Mean density',      value: '3.45 g/cm³' },
              { label: 'Moment of inertia', value: '0.4208 ± 0.0047 (normalised polar)' },
              { label: 'Uniform-body value', value: '0.4505' },
              { label: 'Meteorite link',    value: 'Howardites, eucrites, diogenites' }
            ]
          }
        ]
      },
      {
        id: 'dawn',
        label: 'Dawn at Vesta',
        summary: 'In orbit from 16 July 2011 to 5 September 2012',
        payload: [
          {
            type: 'description',
            text: "Dawn reached Vesta on 16 July 2011 after a four-year cruise that included a Mars gravity assist. It spiralled down through a series of mapping orbits, studying the asteroid's shape, gravity, composition and craters, then left on 5 September 2012 for Ceres. Understanding why Vesta had separated into layers was one of the mission's objectives."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Arrival',          value: '16 July 2011' },
              { label: 'Survey orbit',     value: '2,750 km altitude, 69 hour period (from 2 August 2011)' },
              { label: 'High mapping orbit', value: '680 km, 12.3 hours (from 27 September 2011)' },
              { label: 'Low mapping orbit', value: '210 km, 4.3 hours (from 8 December 2011)' },
              { label: 'Departure',        value: '5 September 2012' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: 4 Vesta', url: 'https://science.nasa.gov/solar-system/asteroids/4-vesta/' },
              { label: 'JPL Small-Body Database: 4 Vesta', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=4' },
              { label: 'NSSDCA: Dawn spacecraft', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=2007-043A' },
              { label: 'NASA: Dawn mission', url: 'https://science.nasa.gov/mission/dawn/' },
              { label: 'NSSDCA: Asteroid fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/asteroidfact.html' },
              { label: 'Park et al. 2025, Nature Astronomy (a small core in Vesta)', url: 'https://doi.org/10.1038/s41550-025-02533-7' },
              { label: 'Vernazza et al. 2021, A&A 654, A56 (sizes and masses)', url: 'https://doi.org/10.1051/0004-6361/202141781' },
              { label: 'USGS Astrogeology: Vesta Dawn FC HAMO Global Mosaic (the surface shown)', url: 'https://astrogeology.usgs.gov/search/map/vesta_dawn_fc_hamo_global_mosaic_60m' }
            ]
          }
        ]
      }
    ]
  },

  pallas: {
    id: 'pallas',
    label: 'Pallas',
    type: 'Main-belt asteroid',
    summary: 'The second asteroid discovered, on a steeply tilted orbit that has left it heavily cratered.',
    payload: [
      {
        type: 'description',
        text: "Pallas is the third largest body in the asteroid belt and the second asteroid ever discovered, found by Heinrich Wilhelm Olbers at Bremen on 28 March 1802. Its orbit is tilted about 35 degrees to the ecliptic, so it crosses the belt at an angle and meets other asteroids at high speed. Images from ESO's Very Large Telescope published in 2020 showed a surface covered in craters, described in the survey's results as golf-ball-like. As of 2020 it was the largest object in the main belt not yet visited by a spacecraft."
      },
      {
        type: 'callout',
        tone: 'info',
        text: "The surface shown here is an artistic rendition. No spacecraft has visited Pallas, and the sharpest pictures, from ESO's Very Large Telescope, show only its largest craters, 36 of them over 30 km across. The shape is the one fitted to those pictures, and the craters and the grey, slightly bluish ground are drawn to match them."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: '511 km (volume-equivalent)' },
          { label: 'Mass',              value: '2.04 × 10²⁰ kg' },
          { label: 'Mean density',      value: '2.92 g/cm³' },
          { label: 'Distance from Sun', value: '2.77 AU (semi-major axis)' },
          { label: 'Orbital period',    value: '1,684 days (4.61 years)' },
          { label: 'Rotation period',   value: '7.813 hours' },
          { label: 'Spectral class',    value: 'B' },
          { label: 'Discovered',        value: '28 March 1802, H. W. Olbers, Bremen' },
          { label: 'Shape',             value: 'Fitted to VLT images (Marsset et al. 2020)' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'orbit',
        label: 'A steeply tilted orbit',
        summary: 'Crossing the belt at an angle, Pallas is hit harder than its neighbours',
        payload: [
          {
            type: 'description',
            text: "Pallas's orbit is inclined about 35 degrees to the ecliptic and is fairly eccentric. Simulations by Marsset and colleagues found that this raises the average speed of impacts on Pallas to about 11.5 km/s, against about 5.8 km/s for the asteroid belt as a whole, and they link that to its heavily cratered surface."
          },
          {
            type: 'stats',
            label: 'Orbit (osculating, epoch 9 June 2026)',
            entries: [
              { label: 'Designation',     value: '2 Pallas (A802 FA)' },
              { label: 'Semi-major axis', value: '2.770 AU' },
              { label: 'Perihelion',      value: '2.131 AU' },
              { label: 'Aphelion',        value: '3.408 AU' },
              { label: 'Eccentricity',    value: '0.231' },
              { label: 'Inclination',     value: '34.93°' },
              { label: 'Orbital period',  value: '1,683.5 days' },
              { label: 'Mean impact speed', value: 'About 11.5 km/s (belt average about 5.8 km/s)' }
            ]
          }
        ]
      },
      {
        id: 'surface',
        label: 'The cratered surface',
        summary: 'Imaged by VLT/SPHERE in 2020: 36 mapped craters, two basins and a bright spot',
        payload: [
          {
            type: 'description',
            text: "Marsset and colleagues imaged Pallas with the SPHERE adaptive optics instrument on ESO's Very Large Telescope, reporting the results in Nature Astronomy on 10 February 2020. They found numerous craters larger than 30 km across, two large impact basins that could be linked to the impact that made the Pallas family of asteroids, and a bright spot. Their crater map marks 36 craters."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Shape',           value: '568 × 532 × 448 km' },
              { label: 'Mapped craters',  value: '36' },
              { label: 'Large basins',    value: '2' },
              { label: 'Geometric albedo', value: '0.155' },
              { label: 'Instrument',      value: 'SPHERE, ESO Very Large Telescope' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The bright spot may mark salts enriched during an early phase of aqueous alteration, which fits the relatively high albedo of Pallas, though the study notes other origins are possible.'
          }
        ]
      },
      {
        id: 'composition',
        label: 'Composition',
        summary: 'Density and spectra like a CM chondrite',
        payload: [
          {
            type: 'description',
            text: "Earlier telescope campaigns gave conflicting densities for Pallas, one close to icy Ceres and one close to rocky Vesta. The VLT shape gave a bulk density of 2.89 ± 0.08 g/cm³, consistent with a body like the CM carbonaceous chondrite meteorites, as its reflectance near 3 micrometres also suggests. Pallas belongs to the B spectral class."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Density (Marsset et al. 2020)', value: '2.89 ± 0.08 g/cm³' },
              { label: 'Density (Vernazza et al. 2021)', value: '2.92 ± 0.08 g/cm³' },
              { label: 'Meteorite analogue', value: 'CM chondrite-like' },
              { label: 'Spectral class',  value: 'B' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'JPL Small-Body Database: 2 Pallas', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=2' },
              { label: 'Marsset et al. 2020, Nature Astronomy 4, 569 (the cratered surface)', url: 'https://doi.org/10.1038/s41550-019-1007-5' },
              { label: 'Vernazza et al. 2021, A&A 654, A56 (VLT/SPHERE survey)', url: 'https://doi.org/10.1051/0004-6361/202141781' },
              { label: 'MIT News: Study reveals details of "golf ball asteroid"', url: 'https://news.mit.edu/2020/pallas-golf-ball-asteroid-0210' },
              { label: 'DAMIT: Pallas model 4395 (the shape shown)', url: 'https://damit.cuni.cz/asteroid_models/view/4395' },
              { label: 'NASA: 4 Vesta (Olbers and Pallas)', url: 'https://science.nasa.gov/solar-system/asteroids/4-vesta/' },
              { label: 'NSSDCA: Asteroid fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/asteroidfact.html' }
            ]
          }
        ]
      }
    ]
  },

  hygiea: {
    id: 'hygiea',
    label: 'Hygiea',
    type: 'Main-belt asteroid (dwarf planet candidate)',
    summary: 'The fourth largest body in the belt, round enough to be a dwarf planet.',
    payload: [
      {
        type: 'description',
        text: "Hygiea is the fourth largest object in the asteroid belt, after Ceres, Vesta and Pallas, a dark body orbiting 3.15 AU from the Sun. A. de Gasparis discovered it from Naples on 12 April 1849. In 2019 images from ESO's Very Large Telescope showed it is nearly spherical, which could make it the smallest dwarf planet in the solar system. As of October 2026, NASA lists five dwarf planets recognised by the IAU, and Hygiea is not among them."
      },
      {
        type: 'callout',
        tone: 'info',
        text: "The surface shown here is an artistic rendition. No spacecraft has visited Hygiea, and the Very Large Telescope pictures that showed its round shape could pick out only two clear craters. The shape is the one fitted to those pictures, and the dark, softly cratered ground is drawn to match a surface with no large basin."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: '433 km (volume-equivalent)' },
          { label: 'Mass',              value: '8.74 × 10¹⁹ kg' },
          { label: 'Mean density',      value: '2.06 g/cm³' },
          { label: 'Distance from Sun', value: '3.15 AU (semi-major axis)' },
          { label: 'Orbital period',    value: '2,043 days (5.59 years)' },
          { label: 'Rotation period',   value: '13.83 hours' },
          { label: 'Spectral class',    value: 'C' },
          { label: 'Discovered',        value: '12 April 1849, A. de Gasparis, Naples' },
          { label: 'Shape',             value: 'Fitted to VLT images (Vernazza et al.)' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'dwarf-planet',
        label: 'A possible dwarf planet',
        summary: 'Nearly as round as Ceres, according to VLT/SPHERE',
        payload: [
          {
            type: 'description',
            text: "As a body in the main belt, Hygiea already meets three of the four requirements to be a dwarf planet: it orbits the Sun, it is not a moon, and it has not cleared the neighbourhood around its orbit. The fourth is enough mass to pull itself into a roughly round shape. Vernazza and colleagues resolved Hygiea's shape for the first time with the SPHERE instrument on ESO's Very Large Telescope and found it nearly as spherical as Ceres, which opens the way for it to be reclassified."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Volume-equivalent radius', value: '217 ± 7 km (Vernazza et al. 2019)' },
              { label: 'Shortest to longest axis', value: '0.94 (Vernazza et al. 2021)' },
              { label: 'Density',         value: '1,944 ± 250 kg/m³ (2019); 2.06 ± 0.20 g/cm³ (2021)' },
              { label: 'Surface coverage', value: '95% observed' },
              { label: 'Published',       value: 'Nature Astronomy, 28 October 2019' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'NASA lists the five dwarf planets the IAU has recognised so far as Ceres, Pluto, Haumea, Makemake and Eris. Hygiea, at just over 430 km across, would be the smallest of them.'
          }
        ]
      },
      {
        id: 'giant-impact',
        label: 'A shattered and reassembled world',
        summary: 'A head-on collision about 2 billion years ago left a round body and nearly 7,000 fragments',
        payload: [
          {
            type: 'description',
            text: "Hygiea is the main member of one of the biggest asteroid families, close to 7,000 bodies that came from the same parent. Astronomers expected the impact that made the family to have left a large basin, as on Vesta, but SPHERE found only two clear craters, both too small. Simulations showed that a head-on collision with a body 75 to 150 km across, about 2 billion years ago, shattered the parent completely, and the pieces that fell back together formed the round Hygiea."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Family members',  value: 'Close to 7,000' },
              { label: 'Impactor size',   value: '75 to 150 km' },
              { label: 'Time of impact',  value: 'About 2 billion years ago' },
              { label: 'Clear craters seen', value: '2' }
            ]
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit, spin and surface',
        summary: 'Outer-belt orbit, a 13.8 hour day and a dark, Ceres-like surface',
        payload: [
          {
            type: 'description',
            text: "Hygiea orbits in the outer belt at 3.15 AU, on an orbit only slightly tilted to the ecliptic. The 2019 study measured its rotation period at about 13.8 hours, half the value accepted before. It is the only known asteroid whose surface composition appears similar to that of Ceres, which suggests the two may share an origin."
          },
          {
            type: 'stats',
            label: 'Orbit (osculating, epoch 9 June 2026)',
            entries: [
              { label: 'Designation',     value: '10 Hygiea (A849 GA)' },
              { label: 'Semi-major axis', value: '3.151 AU' },
              { label: 'Perihelion',      value: '2.815 AU' },
              { label: 'Aphelion',        value: '3.487 AU' },
              { label: 'Eccentricity',    value: '0.107' },
              { label: 'Inclination',     value: '3.83°' },
              { label: 'Orbital period',  value: '2,043.0 days' },
              { label: 'Geometric albedo', value: '0.063' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'ESO: Smallest dwarf planet in the Solar System? (eso1918)', url: 'https://www.eso.org/public/news/eso1918/' },
              { label: 'JPL Small-Body Database: 10 Hygiea', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=10' },
              { label: 'Vernazza et al. 2019, Nature Astronomy 4, 136 (basin-free spherical shape)', url: 'https://doi.org/10.1038/s41550-019-0915-8' },
              { label: 'DAMIT: Hygiea model 4392 (the shape shown)', url: 'https://damit.cuni.cz/asteroid_models/view/4392' },
              { label: 'Vernazza et al. 2021, A&A 654, A56 (VLT/SPHERE survey)', url: 'https://doi.org/10.1051/0004-6361/202141781' },
              { label: 'NASA: Dwarf planets', url: 'https://science.nasa.gov/dwarf-planets/' },
              { label: 'IAU press release iau0603 (dwarf planet definition)', url: 'https://iauarchive.eso.org/news/pressreleases/detail/iau0603/' }
            ]
          }
        ]
      }
    ]
  }
};

// Near-Earth asteroids.
const NEAR_EARTH = {
  'apophis': {
    id: 'apophis',
    label: 'Apophis',
    type: 'Near-Earth asteroid (Aten group)',
    summary: 'A 340 m stony asteroid that will pass closer to Earth than geosynchronous satellites on 13 April 2029.',
    payload: [
      {
        type: 'description',
        text: 'Apophis is a stony near-Earth asteroid about 340 metres across, discovered at Kitt Peak in Arizona on 19 June 2004. Soon after its discovery it was rated one of the most hazardous asteroids that could hit Earth, until further tracking ruled out impacts in 2029, 2036 and finally 2068. On 13 April 2029 it will pass about 32,000 km above Earth\'s surface, closer than satellites in geosynchronous orbit, and should be visible without a telescope from the Eastern Hemisphere.'
      },
      {
        type: 'callout',
        tone: 'info',
        text: "The surface shown here is an artistic rendition. No spacecraft has seen Apophis up close yet, so its shape is the one fitted to how its light changes as it tumbles, and the boulders on it are modelled on Itokawa, a stony rubble pile of about the same size that Japan's Hayabusa visited in 2005. OSIRIS-APEX is due to reach it in 2029 and will show its real surface."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: '340 m (± 40 m)' },
          { label: 'Semi-major axis',   value: '0.922 AU' },
          { label: 'Orbital period',    value: '323.6 days (0.89 years)' },
          { label: 'Rotation',          value: '30.56 h, tumbling' },
          { label: 'Spectral type',     value: 'Sq (stony)' },
          { label: 'Discovered',        value: '19 June 2004, Kitt Peak' },
          { label: '2029 flyby',        value: '13 April 2029, 38,011 km from Earth\'s centre' },
          { label: 'Impact risk',       value: 'None for at least 100 years (NASA, 2021)' },
          { label: 'Shape',             value: 'Fitted to its light curves (Brož et al. 2026)' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'close-approach-2029',
        label: 'The 2029 flyby',
        summary: 'Closer than geosynchronous satellites on 13 April 2029',
        payload: [
          {
            type: 'description',
            text: 'On 13 April 2029 Apophis will come within about 32,000 km of Earth\'s surface. Geosynchronous satellites orbit about 36,000 km up, so the asteroid will pass inside their ring. Observers in the Eastern Hemisphere should be able to see it without a telescope or binoculars. Earth\'s gravity is expected to alter the asteroid\'s orbit, change how fast it spins and possibly set off quakes or landslides on its surface.'
          },
          {
            type: 'stats',
            label: 'Closest approach (JPL)',
            entries: [
              { label: 'Time',                    value: '13 April 2029, 21:46 TDB' },
              { label: 'Distance from centre',    value: '38,011 km (0.000254 AU)' },
              { label: 'Height above surface',    value: 'About 32,000 km' },
              { label: 'Speed relative to Earth', value: '7.42 km/s' },
              { label: 'Timing uncertainty (3σ)', value: 'Under 1 minute' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The flyby will alter its orbit. The Minor Planet Center\'s naming citation notes that, as a result of the 2029 passage, Apophis will move from the Aten class to the Apollo class.'
          }
        ]
      },
      {
        id: 'impact-risk',
        label: 'Impact risk',
        summary: 'Once a top threat, taken off the risk list in 2021',
        payload: [
          {
            type: 'description',
            text: 'After its discovery in 2004 Apophis was identified as one of the most hazardous asteroids that could impact Earth. More observations ruled out an impact in 2029 and then one in 2036, but a small chance of an impact in 2068 remained. When Apophis passed Earth at about 17 million km around 5 March 2021, radar measured its distance to an accuracy of about 150 metres. With recent optical observations, that shrank the uncertainty in its 2029 position from hundreds of kilometres to a handful, and NASA concluded there is no risk of an impact for at least a century.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Radar', secondary: 'The 70 m Goldstone antenna transmitted and the 100 m Green Bank Telescope received, doubling the signal strength' },
              { primary: 'Image resolution', secondary: '38.75 m per pixel from 17 million km away, about 44 times the Earth-Moon distance' },
              { primary: 'Sentry risk table', secondary: 'Apophis was removed from the CNEOS impact risk table in 2021' }
            ]
          }
        ]
      },
      {
        id: 'shape-and-composition',
        label: 'Shape and composition',
        summary: 'A stony, possibly two-lobed body in a tumbling spin',
        payload: [
          {
            type: 'description',
            text: 'Apophis is a stony asteroid of silicate rock mixed with metallic nickel and iron. Radar observations suggest a bilobed, peanut-like shape, which is common among near-Earth asteroids larger than 200 metres: at least one in six of them have two lobes. Its spin is a slow tumble with a period of about 30.6 hours.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Diameter',          value: '340 m (from a radar shape model)' },
              { label: 'Geometric albedo',  value: '0.35' },
              { label: 'Spectral type',     value: 'Sq (SMASSII)' },
              { label: 'Rotation period',   value: '30.56 h' },
              { label: 'Spin state',        value: 'Tumbling' }
            ]
          }
        ]
      },
      {
        id: 'missions',
        label: 'Missions to Apophis',
        summary: 'NASA\'s OSIRIS-APEX and ESA\'s Ramses',
        payload: [
          {
            type: 'description',
            text: 'NASA redirected the OSIRIS-REx spacecraft, which returned samples from Bennu in 2023, to Apophis and renamed it OSIRIS-APEX. It flew within 3,438 km of Earth on 23 September 2025 and is due to rendezvous with Apophis in June 2029, after the flyby. It will study how the encounter changed the asteroid, then dip towards the surface and fire its engines to kick up rocks and dust so scientists can see the material just below. ESA\'s Ramses, a joint mission with JAXA under ESA leadership, is due to launch in April 2028 and reach Apophis in February 2029, two months before the flyby, to record how the asteroid is changed.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'OSIRIS-APEX Earth flyby', value: '23 September 2025, 3,438 km' },
              { label: 'OSIRIS-APEX arrival',     value: 'June 2029' },
              { label: 'OSIRIS-APEX PI',          value: 'Dani Mendoza DellaGiustina' },
              { label: 'Ramses launch',           value: 'April 2028 (planned)' },
              { label: 'Ramses arrival',          value: 'February 2029 (planned)' }
            ]
          }
        ]
      },
      {
        id: 'orbit-and-discovery',
        label: 'Orbit and discovery',
        summary: 'An Aten asteroid named after the Egyptian god of evil',
        payload: [
          {
            type: 'description',
            text: 'Apophis was discovered on 19 June 2004 by Roy Tucker, David Tholen and Fabrizio Bernardi at Kitt Peak National Observatory, and first designated 2004 MN4. It is named after the Egyptian god of evil and destruction, also known as Apep, who dwelled in eternal darkness. Its average distance from the Sun, 0.922 AU, is a little less than Earth\'s.'
          },
          {
            type: 'stats',
            label: 'Orbit (JPL, epoch 2026)',
            entries: [
              { label: 'Semi-major axis',  value: '0.922 AU' },
              { label: 'Perihelion',       value: '0.746 AU' },
              { label: 'Aphelion',         value: '1.099 AU' },
              { label: 'Eccentricity',     value: '0.191' },
              { label: 'Inclination',      value: '3.34°' },
              { label: 'Orbital period',   value: '323.6 days' },
              { label: 'Earth MOID',       value: '0.000108 AU (about 16,000 km)' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Apophis', url: 'https://science.nasa.gov/solar-system/asteroids/apophis/' },
              { label: 'JPL Small-Body Database: 99942 Apophis', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=99942' },
              { label: 'NASA: OSIRIS-APEX', url: 'https://science.nasa.gov/mission/osiris-apex/' },
              { label: 'NASA: Earth is safe from Apophis for 100-plus years (2021)', url: 'https://www.nasa.gov/solar-system/nasa-analysis-earth-is-safe-from-asteroid-apophis-for-100-plus-years/' },
              { label: 'ESA: Ramses, the mission to Apophis', url: 'https://www.esa.int/Space_Safety/Planetary_Defence/Ramses_ESA_s_mission_to_asteroid_Apophis' },
              { label: 'JPL close approach data: Apophis in 2029', url: 'https://ssd-api.jpl.nasa.gov/cad.api?des=99942&date-min=2029-01-01&date-max=2029-12-31&dist-max=0.01' },
              { label: 'JPL Sentry: Apophis removed from the risk table', url: 'https://ssd-api.jpl.nasa.gov/sentry.api?des=99942' },
              { label: 'Pravec et al. (2014), The tumbling spin state of (99942) Apophis, Icarus 233', url: 'https://doi.org/10.1016/j.icarus.2014.01.026' },
              { label: 'DAMIT: Apophis tumbler model 6 (the shape shown)', url: 'https://damit.cuni.cz/tumblers/view/6' }
            ]
          }
        ]
      }
    ]
  },

  'bennu': {
    id: 'bennu',
    label: 'Bennu',
    type: 'Near-Earth asteroid (Apollo group)',
    summary: 'The carbon-rich asteroid that OSIRIS-REx sampled and brought home in 2023.',
    payload: [
      {
        type: 'description',
        text: 'Bennu is a dark, carbon-rich near-Earth asteroid about half a kilometre across that passes close to Earth about every six years. NASA\'s OSIRIS-REx collected rocks and dust from its surface on 20 October 2020 and delivered 121.6 grams of them to Earth on 24 September 2023, the largest asteroid sample ever collected in space. The rocks it is made of formed nearly 4.6 billion years ago on a larger world later destroyed by a giant collision, and Bennu gathered from a small part of that rubble about 1 to 2 billion years ago.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Size',             value: '505 × 492 × 457 m (mean diameter 484 m)' },
          { label: 'Mass',             value: '7.3 × 10¹⁰ kg' },
          { label: 'Bulk density',     value: '1.19 g/cm³' },
          { label: 'Rotation',         value: '4.296 h' },
          { label: 'Semi-major axis',  value: '1.126 AU' },
          { label: 'Orbital period',   value: '436.6 days (1.20 years)' },
          { label: 'Spectral type',    value: 'B (carbonaceous)' },
          { label: 'Discovered',       value: '11 September 1999, LINEAR' },
          { label: 'Shape',            value: 'OSIRIS-REx' },
          { label: 'Surface shown',    value: 'OSIRIS-REx PolyCam mosaic' }
        ]
      }
    ],
    children: [
      {
        id: 'osiris-rex',
        label: 'OSIRIS-REx sample return',
        summary: 'NASA\'s first asteroid sample, collected in 2020 and landed in 2023',
        payload: [
          {
            type: 'description',
            text: 'OSIRIS-REx launched on 8 September 2016 and reached Bennu on 3 December 2018. On 31 December 2018 it entered orbit, making Bennu the smallest object ever orbited by a spacecraft. After a year of mapping, the team chose a sample site called Nightingale, in a 140-metre crater in the northern hemisphere. On 20 October 2020 the sampler head touched the surface for about 6 seconds while a jet of nitrogen gas drove rocks and dust into it. The spacecraft left Bennu on 10 May 2021 and released its capsule over Earth on 24 September 2023, which parachuted to the Utah Test and Training Range.'
          },
          {
            type: 'list',
            entries: [
              { primary: '8 September 2016',  secondary: 'Launch on an Atlas V from Cape Canaveral' },
              { primary: '22 September 2017', secondary: 'Earth gravity assist' },
              { primary: '3 December 2018',   secondary: 'Arrival at Bennu' },
              { primary: '31 December 2018',  secondary: 'First orbit insertion' },
              { primary: '12 December 2019',  secondary: 'Nightingale chosen as the sample site' },
              { primary: '20 October 2020',   secondary: 'Touch-And-Go sample collection' },
              { primary: '10 May 2021',       secondary: 'Departure for Earth' },
              { primary: '24 September 2023', secondary: 'Sample capsule lands in Utah' },
              { primary: '15 February 2024',  secondary: 'Bulk sample mass announced: 121.6 g' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The mission\'s requirement was 60 grams. It returned 121.6 grams, more than twice that. The spacecraft flew on past Earth and is now OSIRIS-APEX, bound for Apophis.'
          }
        ]
      },
      {
        id: 'sample-science',
        label: 'What the sample holds',
        summary: 'Carbon, water-bearing clays, amino acids and salts',
        payload: [
          {
            type: 'description',
            text: 'The first look at the sample in October 2023 found abundant carbon and water-bearing clay minerals. Papers published in Nature and Nature Astronomy in January 2025 reported 14 of the 20 amino acids that life on Earth uses to make proteins, all five nucleobases used in DNA and RNA, and very high levels of ammonia. They also found 11 minerals that form as salty water evaporates, among them trona, never seen before in an extraterrestrial sample. According to NASA they suggest the conditions for life to emerge were widespread in the early solar system. They contain no evidence of life itself.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Amino acids',      secondary: '14 of the 20 that life on Earth uses, in equal left- and right-handed amounts' },
              { primary: 'Nucleobases',      secondary: 'All five used to store genetic instructions in DNA and RNA' },
              { primary: 'Evaporite salts',  secondary: '11 minerals from calcite to halite and sylvite, left by evaporating brine' },
              { primary: 'December 2025',    secondary: 'Papers on sugars essential to life, an ancient gum-like material and presolar grains' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'NASA is keeping at least 70% of the sample at Johnson Space Center for scientists in future decades.'
          }
        ]
      },
      {
        id: 'surface-and-structure',
        label: 'Surface and structure',
        summary: 'Boulder-strewn, very dark and very porous',
        payload: [
          {
            type: 'description',
            text: 'Bennu has a density of only 1.19 g/cm³ and an extremely rocky surface strewn with boulders. It is very dark, with a geometric albedo of 0.044. OSIRIS-REx tracked 313 small particles near Bennu between December 2018 and September 2019: 65% followed suborbital paths back to the surface, 20% orbited the asteroid more than once and 15% escaped. In March 2026 researchers reported that its porous boulders are riddled with networks of cracks, which explains why they lose heat faster than expected.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Geometric albedo', value: '0.044' },
              { label: 'Thermal inertia',  value: '300 J m⁻² s⁻½ K⁻¹' },
              { label: 'Particles tracked', value: '313 (December 2018 to September 2019)' },
              { label: 'Particle paths',   value: '65% suborbital, 20% orbiting, 15% escaping' },
              { label: 'Median particle',  value: '0.74 cm across' }
            ]
          }
        ]
      },
      {
        id: 'impact-risk',
        label: 'Impact risk',
        summary: 'A small chance of impact in the late 2100s',
        payload: [
          {
            type: 'description',
            text: 'Bennu is classed as a potentially hazardous asteroid. Every six years it comes within about 299,000 km of Earth, and JPL\'s close-approach data puts its pass on 25 September 2135 at about 204,000 km. NASA\'s CNEOS Sentry system lists 157 possible impacts between 2178 and 2290, adding up to a probability of about 1 in 1,750. The single most likely date is 24 September 2182, at about 1 in 2,700.'
          },
          {
            type: 'stats',
            label: 'CNEOS Sentry (solution of 14 July 2021)',
            entries: [
              { label: 'Cumulative probability', value: '5.7 × 10⁻⁴ (about 1 in 1,750)' },
              { label: 'Most likely date',       value: '24 September 2182 (3.7 × 10⁻⁴)' },
              { label: 'Possible impacts',       value: '157, between 2178 and 2290' },
              { label: 'Cumulative Palermo scale', value: '−1.40' },
              { label: '2135 close approach',    value: '25 September 2135, about 204,000 km' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Sentry figures as retrieved on 10 October 2026; JPL computed the impact analysis behind them on 14 July 2021. JPL notes that such probabilities can easily be off by a factor of a few.'
          }
        ]
      },
      {
        id: 'orbit-and-name',
        label: 'Orbit and name',
        summary: 'Discovered by LINEAR in 1999, named through a students\' contest',
        payload: [
          {
            type: 'description',
            text: 'Bennu was discovered on 11 September 1999 by the Lincoln Laboratory Near-Earth Asteroid Research team (LINEAR) at Socorro, and first designated 1999 RQ36. Its name was suggested by Michael Puzio, born in 2004, through a naming contest for students. Bennu is an Egyptian mythological figure associated with Osiris, Atum and Ra, and the mission\'s Touch-and-Go sampler evokes Bennu\'s image as a heron.'
          },
          {
            type: 'stats',
            label: 'Orbit (JPL)',
            entries: [
              { label: 'Semi-major axis', value: '1.126 AU' },
              { label: 'Perihelion',      value: '0.897 AU' },
              { label: 'Aphelion',        value: '1.356 AU' },
              { label: 'Eccentricity',    value: '0.204' },
              { label: 'Inclination',     value: '6.03°' },
              { label: 'Orbital period',  value: '436.6 days' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Bennu', url: 'https://science.nasa.gov/solar-system/asteroids/101955-bennu/' },
              { label: 'NASA: OSIRIS-REx', url: 'https://science.nasa.gov/mission/osiris-rex/' },
              { label: 'USGS Astrogeology: Bennu OSIRIS-REx OCAMS Global PAN Mosaic (the surface shown)', url: 'https://astrogeology.usgs.gov/search/map/bennu_osiris_rex_ocams_global_pan_mosaic_5cm' },
              { label: 'JPL Small-Body Database: 101955 Bennu', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=101955' },
              { label: 'CNEOS Sentry: Bennu', url: 'https://cneos.jpl.nasa.gov/sentry/details.html#?des=101955' },
              { label: 'JPL Sentry data: Bennu', url: 'https://ssd-api.jpl.nasa.gov/sentry.api?des=101955' },
              { label: 'JPL close approach data: Bennu in 2135', url: 'https://ssd-api.jpl.nasa.gov/cad.api?des=101955&date-min=2100-01-01&date-max=2200-01-01&dist-max=0.01' },
              { label: 'NSSDCA: OSIRIS-REx', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=2016-055A' },
              { label: 'NSSDCA: Asteroid fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/asteroidfact.html' },
              { label: 'NASA: Bennu sample contains carbon and water (2023)', url: 'https://www.nasa.gov/news-release/nasas-bennu-asteroid-sample-contains-carbon-water/' },
              { label: 'NASA: Bennu sample reveals mix of life\'s ingredients (2025)', url: 'https://www.nasa.gov/news-release/nasas-asteroid-bennu-sample-reveals-mix-of-lifes-ingredients/' },
              { label: 'Chesley et al. (2020), Trajectory estimation for particles observed near Bennu, JGR Planets 125', url: 'https://doi.org/10.1029/2019JE006363' }
            ]
          }
        ]
      }
    ]
  },

  'eros': {
    id: 'eros',
    label: 'Eros',
    type: 'Near-Earth asteroid (Amor group)',
    summary: 'The first near-Earth asteroid found, and the first asteroid a spacecraft orbited and landed on.',
    payload: [
      {
        type: 'description',
        text: 'Eros is an elongated stony asteroid about 34 km long, found from Berlin on 13 August 1898 as the first near-Earth asteroid known. It belongs to the Amor group, whose orbits cross the orbit of Mars and do not quite reach Earth\'s. NASA\'s NEAR Shoemaker went into orbit around it on 14 February 2000 and touched down on its surface on 12 February 2001, the first spacecraft to orbit an asteroid and the first to land on one.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Size',             value: '34.4 × 11.2 × 11.2 km' },
          { label: 'Mean diameter',    value: '16.84 km (sphere of the same volume)' },
          { label: 'Mass',             value: '6.69 × 10¹⁵ kg' },
          { label: 'Bulk density',     value: '2.67 g/cm³' },
          { label: 'Rotation',         value: '5.27 h' },
          { label: 'Semi-major axis',  value: '1.458 AU' },
          { label: 'Orbital period',   value: '643.2 days (1.76 years)' },
          { label: 'Discovered',       value: '13 August 1898, Gustav Witt, Berlin' },
          { label: 'Shape',            value: 'NEAR Shoemaker' },
          { label: 'Surface shown',    value: 'NEAR Shoemaker colour mosaic' }
        ]
      }
    ],
    children: [
      {
        id: 'near-shoemaker',
        label: 'NEAR Shoemaker',
        summary: 'A year in orbit, then the first landing on an asteroid',
        payload: [
          {
            type: 'description',
            text: 'NEAR launched on 17 February 1996, the first mission of NASA\'s Discovery programme. It flew past the asteroid Mathilde in June 1997 and used Earth\'s gravity in January 1998. An aborted engine burn on 20 December 1998 meant it could only fly past Eros, on 23 December 1998, before it finally entered orbit on 14 February 2000, about a year later than planned. It was renamed NEAR Shoemaker on 14 March 2000 in honour of the geologist Eugene Shoemaker. On 12 February 2001 it made a slow, controlled descent and touched down at about 6.4 km/h, taking 69 close-up images on the way. Though never designed as a lander, it survived and sent gamma-ray data from the surface until its last contact on 28 February 2001.'
          },
          {
            type: 'list',
            entries: [
              { primary: '17 February 1996', secondary: 'Launch on a Delta II from Cape Canaveral' },
              { primary: '27 June 1997',     secondary: 'Flyby of asteroid 253 Mathilde at about 1,200 km' },
              { primary: '23 January 1998',  secondary: 'Earth gravity assist at 540 km' },
              { primary: '23 December 1998', secondary: 'Flyby of Eros at 3,827 km from its centre' },
              { primary: '14 February 2000', secondary: 'Orbit insertion, 321 × 366 km' },
              { primary: '12 February 2001', secondary: 'Touchdown on Eros' },
              { primary: '28 February 2001', secondary: 'Last contact' }
            ]
          },
          {
            type: 'stats',
            entries: [
              { label: 'Spacecraft mass', value: '487 kg' },
              { label: 'Images taken', value: 'About 160,000' },
              { label: 'Total cost',   value: '$220.5 million' }
            ]
          }
        ]
      },
      {
        id: 'surface-and-composition',
        label: 'Surface and composition',
        summary: 'Saturated with craters, with a saddle and grooves',
        payload: [
          {
            type: 'description',
            text: 'Most of the surface of Eros is saturated with craters smaller than 1 km across. The largest crater is 5.5 km wide, and a 10 km saddle-like depression looks like a large, worn-down crater. Grooves and ridges run across the surface, and blocks of ejecta 30 to 100 metres across are scattered unevenly over it. Its spectra match an ordinary chondrite composition, and its density implies an interior that is 10 to 30% empty space. NEAR found no magnetic field.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Spectral type',    value: 'S (stony)' },
              { label: 'Geometric albedo', value: '0.25' },
              { label: 'Largest crater',   value: '5.5 km' },
              { label: 'Porosity',         value: '10 to 30%' },
              { label: 'Magnetic field',   value: 'None detected' }
            ]
          }
        ]
      },
      {
        id: 'discovery',
        label: 'Discovery and the astronomical unit',
        summary: 'Found by accident in 1898, then used to measure the solar system',
        payload: [
          {
            type: 'description',
            text: 'Gustav Witt, director of the Urania Observatory in Berlin, found Eros on 13 August 1898 on a two-hour photographic exposure of another asteroid, 185 Eunike, where it left a trail 0.4 mm long. Auguste Charlois found it independently on the same day at Nice. Less than two weeks later Adolf Berberich worked out that its orbit came well inside that of Mars, making it the first near-Earth asteroid known. Breaking with the tradition of the time, it was given a male name, after the Greek god of love.'
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Before radar could reach other worlds, astronomers used Eros to work out the mass of the Earth-Moon system and the length of the astronomical unit.'
          }
        ]
      },
      {
        id: 'close-approaches',
        label: 'Close approaches',
        summary: 'Never closer than about 22 million km',
        payload: [
          {
            type: 'description',
            text: 'Eros is not classed as a potentially hazardous asteroid: the closest its orbit comes to Earth\'s is about 22 million km. In January 1975 it passed about 22 million km away, and astronomers around the world observed it. It will come about as close again in January 2056.'
          },
          {
            type: 'stats',
            label: 'Approaches within 0.2 AU (JPL)',
            entries: [
              { label: '30 January 1931', value: '26.0 million km' },
              { label: '23 January 1975', value: '22.6 million km' },
              { label: '31 January 2012', value: '26.7 million km' },
              { label: '24 January 2056', value: '22.4 million km' },
              { label: '31 January 2093', value: '27.3 million km' },
              { label: 'Earth MOID',      value: '0.149 AU (22.3 million km)' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: 433 Eros', url: 'https://science.nasa.gov/solar-system/asteroids/433-eros/' },
              { label: 'NASA: NEAR Shoemaker', url: 'https://science.nasa.gov/mission/near-shoemaker/' },
              { label: 'JPL Small-Body Database: 433 Eros', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=433' },
              { label: 'NSSDCA: NEAR Shoemaker', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=1996-008A' },
              { label: 'NSSDCA: Asteroid fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/asteroidfact.html' },
              { label: 'JPL close approach data: Eros', url: 'https://ssd-api.jpl.nasa.gov/cad.api?des=433&date-min=1900-01-01&date-max=2100-01-01&dist-max=0.2' },
              { label: 'Veverka et al. (2000), NEAR at Eros: imaging and spectral results, Science 289', url: 'https://doi.org/10.1126/science.289.5487.2088' },
              { label: 'Golish et al. (2023), Eros global albedo maps, Icarus (the surface shown)', url: 'https://doi.org/10.1016/j.icarus.2023.115536' }
            ]
          }
        ]
      }
    ]
  },

  'ryugu': {
    id: 'ryugu',
    label: 'Ryugu',
    type: 'Near-Earth asteroid (Apollo group)',
    summary: 'A spinning-top rubble pile that Japan\'s Hayabusa2 sampled twice.',
    payload: [
      {
        type: 'description',
        text: 'Ryugu is a dark, carbon-rich near-Earth asteroid about 900 metres across, shaped like a spinning top. Japan\'s Hayabusa2 explored it from June 2018 to November 2019, made the first artificial crater on an asteroid and collected 5.4 grams of material in two touchdowns, which a capsule brought down in Australia on 6 December 2020. The samples resemble CI (Ivuna-type) carbonaceous meteorites and are closer to the chemical make-up of the Sun\'s visible surface than any other natural sample.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Mean diameter',    value: '896 m' },
          { label: 'Mass',             value: 'About 4.5 × 10¹¹ kg' },
          { label: 'Bulk density',     value: '1.19 g/cm³' },
          { label: 'Rotation',         value: '7.63 h' },
          { label: 'Semi-major axis',  value: '1.191 AU' },
          { label: 'Orbital period',   value: '474.7 days (1.30 years)' },
          { label: 'Spectral type',    value: 'Cb (carbonaceous)' },
          { label: 'Discovered',       value: '10 May 1999, LINEAR' },
          { label: 'Shape',            value: 'Hayabusa2' },
          { label: 'Surface shown',    value: 'Hayabusa2 mosaic (to about 53° latitude)' }
        ]
      }
    ],
    children: [
      {
        id: 'hayabusa2',
        label: 'Hayabusa2 at Ryugu',
        summary: 'Two touchdowns and a sample capsule landed in Australia',
        payload: [
          {
            type: 'description',
            text: 'JAXA\'s Hayabusa2 launched on 3 December 2014 on an H-IIA rocket and arrived at Ryugu on 27 June 2018, holding station about 20 km away. It touched down and collected surface samples on 22 February 2019, fired an impactor into the surface on 5 April, and on 11 July made a second touchdown to collect material the impact had thrown up from below the surface. It left Ryugu on 13 November 2019 and released its capsule on 5 December 2020, which was recovered the next day at Woomera in Australia.'
          },
          {
            type: 'list',
            entries: [
              { primary: '3 December 2014',  secondary: 'Launch on an H-IIA rocket' },
              { primary: '27 June 2018',     secondary: 'Arrival at Ryugu' },
              { primary: '22 February 2019', secondary: 'First touchdown and surface sample' },
              { primary: '5 April 2019',     secondary: 'Small Carry-on Impactor creates a crater' },
              { primary: '11 July 2019',     secondary: 'Second touchdown, collecting subsurface material' },
              { primary: '13 November 2019', secondary: 'Departure from Ryugu' },
              { primary: '6 December 2020',  secondary: 'Capsule recovered in Australia' }
            ]
          },
          {
            type: 'stats',
            label: 'Sample returned',
            entries: [
              { label: 'Total',             value: '5.4 g (goal: 0.1 g)' },
              { label: 'First touchdown',   value: '3.2 g' },
              { label: 'Second touchdown',  value: '2.0 g' }
            ]
          }
        ]
      },
      {
        id: 'artificial-crater',
        label: 'The artificial crater',
        summary: 'A 2 kg copper impactor at 2 km/s',
        payload: [
          {
            type: 'description',
            text: 'On 5 April 2019 Hayabusa2 released its Small Carry-on Impactor, a 2 kg copper projectile that struck Ryugu at 2 km/s while a deployable camera watched. The crater measured 14.5 metres across at the original surface and 17.6 metres across its raised rim, about seven times larger than the same impact would make on Earth. A boulder to the south blocked its growth, so the crater is a semicircle. Its size showed that gravity, which on Ryugu is about 0.00001 times Earth\'s, controlled how big it grew, which means the surface is very weak.'
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Crater counts calibrated by the experiment suggest Ryugu spent between 6.4 and 11.4 million years in the main asteroid belt before its orbit brought it into the inner solar system.'
          }
        ]
      },
      {
        id: 'composition',
        label: 'Shape and composition',
        summary: 'Loose rubble rich in water-bearing minerals and organic matter',
        payload: [
          {
            type: 'description',
            text: 'Hayabusa2\'s measurements showed that Ryugu is a rubble pile of loose rocks, pulled into its spinning-top shape during an earlier period of rapid spin. It probably formed when rubble thrown out by an impact on a larger asteroid gathered together again. In the laboratory the grains turned out to be mostly minerals formed in water on a parent body, at about 37 °C, some 5.2 million years after the first solids of the solar system. Since then they have probably never been heated above about 100 °C.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Geometric albedo', value: '0.045' },
              { label: 'Particle density', value: '1,282 kg/m³, lower than any meteorite' },
              { label: 'Particle porosity', value: 'About 46% (estimated)' },
              { label: 'Closest meteorite type', value: 'CI (Ivuna-type) carbonaceous chondrites' },
              { label: 'Found in samples', value: 'Water-bearing minerals, organic matter, amino acids' }
            ]
          }
        ]
      },
      {
        id: 'extended-mission',
        label: 'Hayabusa2 after Ryugu',
        summary: 'A record close flyby in 2026, a tiny fast spinner in 2031',
        payload: [
          {
            type: 'description',
            text: 'After dropping off its capsule, Hayabusa2 set out on an extended mission using the xenon left for its ion engines. On 5 July 2026 it flew past the asteroid Torifune (2001 CC21) about 744 metres from its centre and about 400 metres from its surface, the closest pass of any flyby of a solar system body. Earth swing-bys are planned for 2027 and 2028, and it is due to reach 1998 KY26 in 2031. That asteroid is only about 11 metres across and spins once about every 5 minutes.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Torifune flyby',  value: '5 July 2026, about 744 m from centre' },
              { label: 'Torifune size',   value: 'About 840 × 340 m' },
              { label: '1998 KY26 arrival', value: '2031' },
              { label: '1998 KY26 size',  value: 'About 11 m' }
            ]
          }
        ]
      },
      {
        id: 'orbit-and-name',
        label: 'Orbit and name',
        summary: 'Named after the Dragon God\'s undersea palace',
        payload: [
          {
            type: 'description',
            text: 'Ryugu was discovered on 10 May 1999 by the LINEAR team at Socorro, and first designated 1999 JU3. Ryugu is the undersea palace of the Dragon God: in a Japanese legend a fisherman is rewarded with a visit to it and brings back a box with a secret. The naming citation adds that Hayabusa2 would visit Ryugu and bring back a capsule of surface samples. JPL classes it as a potentially hazardous asteroid.'
          },
          {
            type: 'stats',
            label: 'Orbit (JPL, epoch 2026)',
            entries: [
              { label: 'Semi-major axis', value: '1.191 AU' },
              { label: 'Perihelion',      value: '0.963 AU' },
              { label: 'Aphelion',        value: '1.418 AU' },
              { label: 'Eccentricity',    value: '0.191' },
              { label: 'Inclination',     value: '5.87°' },
              { label: 'Orbital period',  value: '474.7 days' },
              { label: 'Earth MOID',      value: '0.00054 AU (about 81,000 km)' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'JAXA: Asteroid Explorer Hayabusa2', url: 'https://global.jaxa.jp/projects/sas/hayabusa2/' },
              { label: 'JPL Small-Body Database: 162173 Ryugu', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=162173' },
              { label: 'ISAS: Hayabusa2 mission page', url: 'https://www.isas.jaxa.jp/en/missions/spacecraft/current/hayabusa2.html' },
              { label: 'JAXA DARTS: Hayabusa2 map products (the surface shown)', url: 'https://data.darts.isas.jaxa.jp/pub/hayabusa2/map_product/' },
              { label: 'JAXA: Hayabusa2 arrives at Ryugu (2018)', url: 'https://global.jaxa.jp/press/2018/06/20180627_hayabusa2.html' },
              { label: 'ISAS: Creating a crater to constrain the age of an asteroid\'s surface', url: 'https://www.isas.jaxa.jp/en/topics/002346.html' },
              { label: 'ISAS: Ryugu is a primitive asteroid rich in water and organic matter', url: 'https://www.isas.jaxa.jp/en/topics/002893.html' },
              { label: 'ISAS: Chemical analysis team results on Ryugu', url: 'https://www.isas.jaxa.jp/en/topics/003094.html' },
              { label: 'JAXA: Hayabusa2 record close flyby of Torifune (2026)', url: 'https://global.jaxa.jp/press/2026/07/20260730-1_e.html' },
              { label: 'Watanabe et al. (2019), Hayabusa2 arrives at Ryugu, a spinning top-shaped rubble pile, Science 364', url: 'https://doi.org/10.1126/science.aav8032' },
              { label: 'Yokoyama et al. (2023), Ryugu samples are similar to Ivuna-type carbonaceous meteorites, Science 379', url: 'https://doi.org/10.1126/science.abn7850' }
            ]
          }
        ]
      }
    ]
  }
};

// Comets.
const COMETS = {
  "halley's comet": {
    id: "halley's comet",
    label: "Halley's Comet",
    type: 'Periodic comet (1P/Halley, Halley-type)',
    summary: 'The first comet known to come back, every 76 years or so, next due at the Sun on 28 July 2061.',
    payload: [
      {
        type: 'description',
        text: "Halley's Comet (1P/Halley) was the first comet understood to be a repeat visitor: in 1705 Edmond Halley linked the bright comets of 1531, 1607 and 1682 and predicted a return in 1758. It comes back about every 76 years on a backward-running orbit that reaches beyond Neptune, and records of it go back more than 2,000 years. At its last return in 1986 six spacecraft met it, and ESA's Giotto took the first close-up pictures of a comet's nucleus. It passed the far end of its orbit in December 2023 and is now falling back towards the Sun."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Nucleus',          value: '15 × 8 km (about 11 km effective diameter)' },
          { label: 'Orbital period',   value: 'About 76 years (74.42 to 79.25 between returns)' },
          { label: 'Last perihelion',  value: '9 February 1986, 0.587 AU (87.8 million km)' },
          { label: 'Last aphelion',    value: '9 December 2023, 35.1 AU (5.26 billion km)' },
          { label: 'Next perihelion',  value: '28 July 2061 (JPL Horizons prediction)' },
          { label: 'Inclination',      value: '162.2° (retrograde, 18° from the plane of Earth\'s orbit)' },
          { label: 'Distance from Sun (as of 10 October 2026)', value: '35.0 AU (5.24 billion km), inbound' },
          { label: 'Recovered',        value: '25 December 1758, by Johann Georg Palitzsch' }
        ]
      }
    ],
    children: [
      {
        id: 'nucleus',
        label: 'The nucleus',
        summary: 'A dark, potato-shaped nucleus about 15 km long',
        payload: [
          {
            type: 'description',
            text: "Giotto's images in March 1986 showed the shape of a comet nucleus for the first time: a black, potato-shaped body with active regions firing jets of gas and dust into space. Halley is one of the darkest objects in the solar system, reflecting only 3 to 4% of the sunlight that falls on it. Giotto also found the first evidence of organic material in a comet."
          },
          {
            type: 'stats',
            label: 'Physical',
            entries: [
              { label: 'Dimensions',         value: '14.9 × 8.2 km (Keller et al. 1987)' },
              { label: 'Effective diameter', value: '11 km (Lamy et al. 2004)' },
              { label: 'Geometric albedo',   value: '0.04 (JPL); NASA gives 0.03' },
              { label: 'Surface lost per orbit', value: '1 to 3 m (NASA estimate)' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'NASA estimates that an average periodic comet lives to complete about 1,000 trips around the Sun. Halley has been in its present orbit for at least 16,000 years and has shown no obvious signs of ageing in its recorded appearances.'
          }
        ]
      },
      {
        id: 'activity',
        label: 'Activity and meteor showers',
        summary: 'Jets, a dusty coma, and two meteor showers a year',
        payload: [
          {
            type: 'description',
            text: "Each time Halley returns to the inner solar system its nucleus sprays ice and rock into space. Giotto recorded about 12,000 dust impacts crossing the coma in 1986, the first 122 minutes before closest approach and the last 49 minutes after. The debris spread along the orbit gives Earth two weak meteor showers every year."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Eta Aquarids', secondary: 'May, from dust shed by Halley' },
              { primary: 'Orionids',     secondary: 'October, from the same debris stream' }
            ]
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit and returns',
        summary: 'A backward 76-year loop from inside the orbit of Venus to beyond Neptune',
        payload: [
          {
            type: 'description',
            text: "Halley goes round the Sun in the opposite direction to Earth, on an orbit tilted 18° to Earth's. At perihelion it comes well inside the orbit of Venus; at aphelion it is beyond Neptune. The planets' gravity changes the period from one return to the next: measured perihelion to perihelion it has been as short as 74.42 years (1835 to 1910) and as long as 79.25 years (451 to 530)."
          },
          {
            type: 'stats',
            label: 'Orbit',
            entries: [
              { label: 'Semi-major axis',  value: '17.9 AU' },
              { label: 'Eccentricity',     value: '0.968' },
              { label: 'Speed at perihelion (1986)', value: '54.55 km/s' },
              { label: 'Speed at aphelion (1948)',   value: '0.91 km/s' },
              { label: 'Last aphelion',    value: '9 December 2023, 35.1 AU' },
              { label: 'Next perihelion',  value: '28 July 2061, 0.593 AU (88.7 million km)' }
            ]
          },
          {
            type: 'list',
            entries: [
              { primary: '10 April 837',  secondary: 'Closest known approach to Earth: 0.033 AU (4.94 million km), about magnitude −3.5' },
              { primary: '10 April 1986', secondary: 'Closest to Earth on the way out: 0.417 AU (62.4 million km)' },
              { primary: '29 July 2061',  secondary: 'Predicted closest to Earth: 0.477 AU (71.4 million km)' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'As of 10 October 2026, JPL Horizons places Halley 35.0 AU (5.24 billion km) from the Sun, a little past the far end of its orbit and moving slowly back inwards.'
          }
        ]
      },
      {
        id: 'history',
        label: 'A comet that comes back',
        summary: "Edmond Halley's prediction and 2,000 years of records",
        payload: [
          {
            type: 'description',
            text: "Until the time of Edmond Halley (1656 to 1742), comets were believed to pass through the solar system only once. In 1705 he used Isaac Newton's theories of gravitation and planetary motion to compute the orbits of several comets, found that the bright comets of 1531, 1607 and 1682 followed similar paths, and predicted that the comet would return in 1758. It did, and history's first known periodic comet was named after him."
          },
          {
            type: 'list',
            entries: [
              { primary: '1066',               secondary: 'Shown in the Bayeux Tapestry, which chronicles the Battle of Hastings' },
              { primary: '1301',               secondary: 'Seen by the painter Giotto di Bondone, who painted it as the star of Bethlehem' },
              { primary: '1531, 1607, 1682',   secondary: 'The three bright comets Halley suggested were one comet making return trips' },
              { primary: '25 December 1758',   secondary: 'The predicted return, found by Johann Georg Palitzsch' },
              { primary: '1986',               secondary: 'The last return, met by an international fleet of spacecraft' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The P in 1P/Halley marks a periodic comet, one with an orbital period of less than 200 years.'
          }
        ]
      },
      {
        id: 'missions',
        label: 'The Halley Armada',
        summary: 'Six spacecraft met the comet in March 1986',
        payload: [
          {
            type: 'description',
            text: "The 1986 return was met by six spacecraft, known as the Halley Armada: Europe's Giotto, the Soviet Union's Vega 1 and Vega 2, Japan's Suisei and Sakigake, and NASA's International Cometary Explorer (ICE). The Vegas went first as pathfinders to locate the nucleus, so that Giotto could home in on it with great accuracy. NASA's Pioneer 7 and Pioneer 12 also added data."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Vega 1',   secondary: '8,890 km from the nucleus on 6 March 1986, after leaving a lander on Venus' },
              { primary: 'Suisei',   secondary: '151,000 km on 8 March 1986; its ultraviolet images measured the rotation and changes in water output' },
              { primary: 'Vega 2',   secondary: '8,030 km on 9 March 1986, also after a Venus lander drop' },
              { primary: 'Sakigake', secondary: 'About 7 million km on 11 March 1986, Japan\'s test probe, studying radio and plasma waves' },
              { primary: 'Giotto',   secondary: '596 km on 13 March 1986, the closest of the fleet' },
              { primary: 'ICE',      secondary: 'About 28 million km in March 1986, after crossing the tail of Comet Giacobini-Zinner on 11 September 1985' }
            ]
          }
        ],
        children: [
          {
            id: 'giotto',
            label: 'Giotto',
            summary: "ESA's first deep-space mission and the closest pass of Halley",
            payload: [
              {
                type: 'description',
                text: "Giotto launched on an Ariane 1 on 2 July 1985 and met Halley at a relative speed of 68 km/s. A two-sheet shield, 1 mm of aluminium in front of 12 mm of Kevlar, took the dust impacts. Seconds before closest approach a particle of about a gram jolted the spacecraft and contact was lost; over the next 32 minutes its thrusters steadied it, by which time it had passed 596 km from the nucleus. Revived in 1990, it flew about 200 km from Comet Grigg-Skjellerup on 10 July 1992."
              },
              {
                type: 'stats',
                entries: [
                  { label: 'Launch',            value: '2 July 1985, Ariane 1 (flight V14)' },
                  { label: 'Mass',              value: '960 kg' },
                  { label: 'Halley encounter',  value: '13 March 1986, 596 km' },
                  { label: 'Relative speed',    value: '68 km/s' },
                  { label: 'Images returned',   value: 'About 2,000' },
                  { label: 'Grigg-Skjellerup',  value: '10 July 1992, about 200 km' }
                ]
              },
              {
                type: 'callout',
                tone: 'info',
                text: "The mission is named after the Italian painter Giotto di Bondone, who saw Halley's Comet in 1301 and painted it as the star of Bethlehem in his Adoration of the Magi in the Scrovegni Chapel."
              }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: 1P/Halley',                      url: 'https://science.nasa.gov/solar-system/comets/1p-halley/' },
              { label: 'JPL Small-Body Database: 1P/Halley',   url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=1P' },
              { label: 'ESA: Giotto overview',                 url: 'https://www.esa.int/Science_Exploration/Space_Science/Giotto_overview' },
              { label: 'ESA: Giotto background science (the Halley fleet)', url: 'https://sci.esa.int/web/giotto/-/31784-background-science' },
              { label: 'NSSDCA: Giotto',                       url: 'https://nssdc.gsfc.nasa.gov/planetary/giotto.html' },
              { label: 'NSSDCA: Comet fact sheet',             url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/cometfact.html' },
              { label: 'NSSDCA: International Cometary Explorer', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=1978-079A' },
              { label: 'NASA: Giotto',                         url: 'https://science.nasa.gov/mission/giotto/' },
              { label: 'NASA: Sakigake',                       url: 'https://science.nasa.gov/mission/sakigake/' },
              { label: 'ISAS/JAXA: Suisei',                    url: 'https://www.isas.jaxa.jp/en/missions/spacecraft/past/suisei.html' },
              { label: 'ISAS/JAXA: Sakigake',                  url: 'https://www.isas.jaxa.jp/en/missions/spacecraft/past/sakigake.html' },
              { label: 'JPL Horizons (perihelion, aphelion and distance dates)', url: 'https://ssd.jpl.nasa.gov/horizons/' }
            ]
          }
        ]
      }
    ]
  },

  'hale-bopp': {
    id: 'hale-bopp',
    label: 'Hale-Bopp',
    type: 'Long-period comet (C/1995 O1)',
    summary: 'The Great Comet of 1997, with a 60 km nucleus, visible to the naked eye for 18 months.',
    payload: [
      {
        type: 'description',
        text: 'Comet Hale-Bopp (C/1995 O1), also called the Great Comet of 1997, was found on 23 July 1995 by Alan Hale and Thomas Bopp, working independently. It was discovered 7.15 AU from the Sun, and its nucleus is about 60 km across; NASA credits that size for its 18 months of naked-eye visibility in 1996 and 1997. It will not return for more than two thousand years.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Nucleus diameter', value: 'About 60 km' },
          { label: 'Discovered',       value: '23 July 1995, by Alan Hale and Thomas Bopp' },
          { label: 'Perihelion',       value: '1 April 1997, 0.914 AU (136.8 million km)' },
          { label: 'Closest to Earth', value: '22 March 1997, 1.315 AU (196.7 million km)' },
          { label: 'Orbital period',   value: 'About 2,534 years (NASA)' },
          { label: 'Inclination',      value: '89.3°' },
          { label: 'Distance from Sun (as of 10 October 2026)', value: '51.2 AU (7.66 billion km), outbound' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Discovery',
        summary: 'Two observers, two states, one night',
        payload: [
          {
            type: 'description',
            text: 'On 23 July 1995 Alan Hale, at Cloudcroft, New Mexico, with a 0.41 m reflector, and Thomas Bopp, observing with J. Stevens near Stanfield, Arizona, through a 0.44 m Dobsonian, each reported a new comet; Bopp found it while observing M70. The IAU circular that announced it described a diffuse comet of about magnitude 10.5 with some condensation and no tail. It was 7.15 AU from the Sun at the time.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Designation',           value: 'C/1995 O1' },
              { label: 'Announced',             value: 'IAU Circular 6187' },
              { label: 'Distance at discovery', value: '7.15 AU from the Sun' },
              { label: 'Brightness at discovery', value: 'About magnitude 10.5' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The C marks a long-period comet, 1995 is the year of discovery, and O1 records the half-month of discovery and that it was the first comet announced in that half-month.'
          }
        ]
      },
      {
        id: 'nucleus',
        label: 'The nucleus',
        summary: 'About 60 km across, with jets thrown off in a pinwheel',
        payload: [
          {
            type: 'description',
            text: "Hale-Bopp's nucleus is about 60 km in diameter, about five times the size of the object thought to have ended the dinosaurs. Its geometric albedo is 0.04, so it reflects about 4% of the light that falls on it. Hubble Space Telescope images showed material thrown off the rotating nucleus in a pinwheel pattern, and the nucleus showed sudden brief eruptions and a complex, mottled surface."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Diameter',         value: 'About 60 km (Fernandez 2002)' },
              { label: 'Geometric albedo', value: '0.04' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "The size estimate grew as the data improved: NSSDCA's 1997 page gave 30 to 40 km, and the later figure of about 60 km (Fernandez 2002) is the one NASA and JPL use now."
          }
        ]
      },
      {
        id: 'apparition-1997',
        label: 'The 1997 apparition',
        summary: 'Eighteen months to the naked eye, and a third kind of tail',
        payload: [
          {
            type: 'description',
            text: 'Hale-Bopp stayed visible to the naked eye for 18 months across 1996 and 1997. It passed closest to Earth on 22 March 1997, at 1.3 AU, and closest to the Sun on 1 April. Alongside its ion and dust tails, Cremonese and colleagues (1997) found a third kind of tail made of neutral sodium atoms, consistent with sodium pushed away from the Sun by the pressure of sunlight.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Closest to Earth', value: '22 March 1997, 1.315 AU' },
              { label: 'Perihelion',       value: '1 April 1997, 03:19 UT, 0.914 AU' }
            ]
          },
          {
            type: 'list',
            entries: [
              { primary: 'Ion tail',    secondary: 'Gas ionised by sunlight' },
              { primary: 'Dust tail',   secondary: 'Grains released from the nucleus' },
              { primary: 'Sodium tail', secondary: 'Neutral sodium pushed out by radiation pressure, which its discoverers called a third type of tail' },
              { primary: 'Observed by', secondary: 'The Hubble Space Telescope, the International Ultraviolet Explorer and sounding rockets launched from White Sands on 24, 25 and 29 March and 5 April 1997' }
            ]
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit',
        summary: 'Steep, very long, and passing near Jupiter in 1996',
        payload: [
          {
            type: 'description',
            text: "Hale-Bopp's orbit stands almost at right angles to the planets', at 89.3°, and is very elongated, with an eccentricity of 0.995. On the way in it passed 0.77 AU from Jupiter on 5 April 1996. NSSDCA puts its previous perihelion about 4,200 years before 1997 and the next about 2,380 years after; NASA gives a period of about 2,534 years. As of 10 October 2026 it is 51.2 AU from the Sun and still moving outwards."
          },
          {
            type: 'stats',
            label: 'Orbit',
            entries: [
              { label: 'Inclination',        value: '89.3°' },
              { label: 'Eccentricity',       value: '0.995' },
              { label: 'Perihelion distance', value: '0.914 AU (1997)' },
              { label: 'Aphelion',           value: 'About 354 AU (JPL orbit for epoch 15 September 2022)' },
              { label: 'Closest to Jupiter', value: '5 April 1996, 0.77 AU (115 million km)' },
              { label: 'Previous perihelion', value: 'About 4,200 years before 1997' },
              { label: 'Next perihelion',    value: 'About 2,380 years after 1997' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "JPL's current orbit uses observations from 27 April 1993, two years before the discovery, to 9 July 2022, nearly 27 years after it."
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Comet Hale-Bopp',                   url: 'https://science.nasa.gov/solar-system/comets/c-1995-o1-hale-bopp/' },
              { label: 'JPL Small-Body Database: C/1995 O1',      url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=C%2F1995%20O1' },
              { label: 'NSSDCA: Comet Hale-Bopp',                 url: 'https://nssdc.gsfc.nasa.gov/planetary/halebopp.html' },
              { label: 'IAU Circular 6187 (the discovery)',       url: 'http://www.cbat.eps.harvard.edu/iauc/06100/06187.html' },
              { label: 'Cremonese et al. 1997, the sodium tail (ApJ 490, L199)', url: 'https://doi.org/10.1086/311040' },
              { label: 'JPL Horizons (perihelion, Jupiter pass and distance)', url: 'https://ssd.jpl.nasa.gov/horizons/' }
            ]
          }
        ]
      }
    ]
  },

  neowise: {
    id: 'neowise',
    label: 'NEOWISE',
    type: 'Long-period comet (C/2020 F3)',
    summary: 'The bright comet of July 2020, found by an infrared space telescope and back in about 6,800 years.',
    payload: [
      {
        type: 'description',
        text: "Comet NEOWISE (C/2020 F3) was discovered on 27 March 2020 by NASA's NEOWISE infrared space telescope, as the comet headed towards the Sun. It passed 0.29 AU from the Sun on 3 July 2020 and became the brightest comet in Northern Hemisphere skies in decades, photographed from the ground, from the International Space Station and by spacecraft that watch the Sun. Its nucleus is roughly 5 km across, and its long orbit will not bring it back for about 6,800 years."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Nucleus diameter', value: 'About 5 km' },
          { label: 'Discovered',       value: '27 March 2020, by the NEOWISE space telescope' },
          { label: 'Perihelion',       value: '3 July 2020, 0.295 AU (44.1 million km)' },
          { label: 'Closest to Earth', value: '23 July 2020 (UT), 0.692 AU (103.5 million km)' },
          { label: 'Orbital period',   value: 'About 6,800 years' },
          { label: 'Inclination',      value: '128.9° (retrograde)' },
          { label: 'Distance from Sun (as of 10 October 2026)', value: '18.7 AU (2.80 billion km), outbound' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Discovery',
        summary: 'Found in infrared images by an asteroid-hunting telescope',
        payload: [
          {
            type: 'description',
            text: "The comet appeared as a string of fuzzy red dots in infrared images NEOWISE took on 27 March 2020, its coma of gas and dust already visible. It looked redder than the background stars and galaxies because it is much cooler and gives off more light at longer wavelengths. On 31 March J. Masiero reported for the NEOWISE team that it showed clear signs of cometary activity, and the Minor Planet Center announced it on 1 April 2020."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Designation',        value: 'C/2020 F3 (NEOWISE)' },
              { label: 'Discovery images',   value: '27 March 2020' },
              { label: 'Activity reported',  value: '31 March 2020' },
              { label: 'Announced',          value: 'MPEC 2020-G05, 1 April 2020' },
              { label: 'Wavelengths',        value: '3.4 and 4.6 micron infrared channels' }
            ]
          }
        ],
        children: [
          {
            id: 'neowise-telescope',
            label: 'The NEOWISE telescope',
            summary: 'An infrared sky surveyor turned asteroid and comet hunter',
            payload: [
              {
                type: 'description',
                text: "The spacecraft began as WISE (Wide-field Infrared Survey Explorer), an infrared space telescope that mapped the sky from December 2009 until it was put into hibernation in February 2011. Reactivated in late 2013, it was given a new job as NEOWISE, finding near-Earth asteroids and comets. NASA ended the mission on 31 July 2024, switched the spacecraft off for the last time on 8 August, and it burned up in Earth's atmosphere on 1 November 2024."
              },
              {
                type: 'stats',
                entries: [
                  { label: 'Launch',            value: '14 December 2009, 14:09 UT' },
                  { label: 'First hibernation', value: '17 February 2011' },
                  { label: 'Reactivated',       value: 'Late 2013, as NEOWISE' },
                  { label: 'Mission ended',     value: '31 July 2024' },
                  { label: 'Decommissioned',    value: '8 August 2024' },
                  { label: 'Re-entry',          value: '1 November 2024' }
                ]
              }
            ]
          }
        ]
      },
      {
        id: 'tails',
        label: 'Nucleus and tails',
        summary: 'A 5 km nucleus, a dust tail, and an ion tail that may be split in two',
        payload: [
          {
            type: 'description',
            text: "NEOWISE's nucleus is roughly 5 km across, and its dust and ion tails stretched hundreds of thousands to millions of kilometres, pointing away from the Sun. On 5 July 2020, just after perihelion, the WISPR camera on NASA's Parker Solar Probe caught both tails from an unmatched vantage point. Its images appear to show a divide in the ion tail, which could mean the comet had two ion tails, though confirming it would need more data."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Dust tail',  secondary: 'Broad and fuzzy: dust lifted off the nucleus that trails behind the comet along its orbit' },
              { primary: 'Ion tail',   secondary: 'Thinner: gas ionised by sunlight and pushed by the solar wind directly away from the Sun' },
              { primary: 'A second ion tail?', secondary: 'Suggested by Parker Solar Probe images, unconfirmed' }
            ]
          }
        ]
      },
      {
        id: 'apparition-2020',
        label: 'Summer 2020',
        summary: 'Perihelion on 3 July, then evening skies from mid-July',
        payload: [
          {
            type: 'description',
            text: "NEOWISE reached perihelion on 3 July 2020 and then turned back towards the outer solar system. On 5 July an astronaut on the International Space Station took more than 340 photos of it rising above Earth's sunlit limb while the station passed over Uzbekistan and central Asia. It came closest to Earth on 23 July (UT), at a harmless 103 million km. NEOWISE co-investigator Emily Kramer said the last comet this bright had been Hale-Bopp."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Perihelion',       value: '3 July 2020, 0.295 AU' },
              { label: 'Closest to Earth', value: '23 July 2020 (UT), 0.692 AU' }
            ]
          },
          {
            type: 'list',
            entries: [
              { primary: 'Parker Solar Probe',  secondary: 'WISPR images of both tails, 5 July 2020' },
              { primary: 'STEREO',              secondary: "NASA's Solar and Terrestrial Relations Observatory" },
              { primary: 'SOHO',                secondary: 'The ESA/NASA Solar and Heliospheric Observatory' },
              { primary: 'International Space Station', secondary: 'Expedition 63 crew photographs, 5 July 2020' }
            ]
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit',
        summary: 'A tilted, backward orbit reaching about 700 AU',
        payload: [
          {
            type: 'description',
            text: "NEOWISE moves round the Sun on a long, steeply tilted orbit with an inclination of 128.9°, so it travels in the opposite direction to the planets. NASA gives about 6,800 years until it returns to the inner solar system. JPL's orbit fitted for 6 July 2020 gives a period of about 6,787 years and an aphelion of about 717 AU for that date. As of 10 October 2026 it is 18.7 AU from the Sun, heading outwards."
          },
          {
            type: 'stats',
            label: 'Orbit',
            entries: [
              { label: 'Perihelion distance', value: '0.295 AU' },
              { label: 'Eccentricity',        value: '0.9992' },
              { label: 'Inclination',         value: '128.9°' },
              { label: 'Aphelion',            value: 'About 717 AU (JPL orbit for epoch 6 July 2020)' },
              { label: 'Period',              value: 'About 6,800 years (NASA); 6,787 years (JPL, epoch 6 July 2020)' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Earth Observatory: Comet NEOWISE brightens the night', url: 'https://science.nasa.gov/earth/earth-observatory/comet-neowise-brightens-the-night-146996/' },
              { label: 'JPL Small-Body Database: C/2020 F3',  url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=C%2F2020%20F3' },
              { label: "NASA: Parker Solar Probe spies Comet NEOWISE", url: 'https://www.nasa.gov/science-research/heliophysics/nasas-parker-solar-probe-spies-newly-discovered-comet-neowise/' },
              { label: 'Minor Planet Center: MPEC 2020-G05',  url: 'https://minorplanetcenter.net/mpec/K20/K20G05.html' },
              { label: 'NASA: NEOWISE mission',               url: 'https://science.nasa.gov/mission/neowise/' },
              { label: 'JPL Horizons (perihelion, Earth approach and distance)', url: 'https://ssd.jpl.nasa.gov/horizons/' }
            ]
          }
        ]
      }
    ]
  }
};

// The interstellar visitors: objects on unbound paths from another star.
// Positions, distances and speeds computed from JPL's paths were read on
// 3 November 2026 (docs/new-objects-verification.md).
const INTERSTELLAR = {
  'oumuamua': {
    id: 'oumuamua',
    label: 'ʻOumuamua',
    type: 'Interstellar object (1I/2017 U1)',
    summary: 'The first object known to have come from another star, found in October 2017 as it sped away from the Sun.',
    payload: [
      {
        type: 'description',
        text: "ʻOumuamua was the first object seen passing through the solar system from interstellar space. The University of Hawaii's Pan-STARRS1 telescope found it on 19 October 2017, more than a month after it had swung past the Sun on 9 September, and the International Astronomical Union named it 1I/2017 U1. Its name is Hawaiian for \"a messenger from afar arriving first\". It came in from about the direction of Vega in Lyra and is leaving for good, too fast for the Sun to hold."
      },
      {
        type: 'callout',
        tone: 'info',
        text: 'The shape shown here is an artistic rendition. No picture resolves ʻOumuamua; its shape is worked out from how its brightness changed as it turned, and NASA describes a reddish rocky body up to 400 m long and perhaps ten times as long as it is wide, which is what is drawn.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Designation',       value: '1I/2017 U1' },
          { label: 'Size',              value: 'Up to 400 m long, perhaps 10 times as long as wide' },
          { label: 'Discovered',        value: '19 October 2017, Pan-STARRS1 (University of Hawaii)' },
          { label: 'Perihelion',        value: '9 September 2017, 0.256 AU (38.3 million km)' },
          { label: 'Eccentricity',      value: '1.20 (unbound)' },
          { label: 'Speed past the Sun', value: '87.3 km/s at perihelion, 26.4 km/s far out' },
          { label: 'Came from',         value: 'Near Vega, in Lyra' },
          { label: 'Distance from Sun (as of 3 November 2026)', value: '55 AU, outbound' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Found on the way out',
        summary: 'Seen only after it had passed the Sun and the Earth',
        payload: [
          {
            type: 'description',
            text: "Pan-STARRS1, a survey telescope funded by NASA's Near-Earth Object Observations Program, picked up ʻOumuamua on 19 October 2017. By then it had already rounded the Sun, on 9 September at 0.256 AU, and passed the Earth, closest on 14 October at 0.162 AU (24 million km). JPL's orbit for it rests on 207 observations made over the 80 days from 14 October 2017, in images taken before it was spotted, to 2 January 2018."
          },
          {
            type: 'stats',
            entries: [
              { label: 'First observation used', value: '14 October 2017 (JPL orbit solution 16)' },
              { label: 'Last observation used',  value: '2 January 2018' },
              { label: 'Observations',           value: '207 over 80 days' },
              { label: 'Closest to Earth',       value: '14 October 2017, 0.162 AU (24.2 million km)' }
            ]
          }
        ]
      },
      {
        id: 'strange',
        label: 'A strange visitor',
        summary: 'Long, reddish, and pushed by something unseen',
        payload: [
          {
            type: 'description',
            text: 'NASA describes ʻOumuamua as rocky and cigar-shaped, perhaps ten times as long as it is wide, reddish like objects in the outer solar system, and spinning every 7.3 hours. Not the faintest hint of dust was seen round it, yet it was moving slightly faster than the Sun\'s pull alone explains, a push of the kind comets get from the gas they give off.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Colour',           value: 'Reddish, like outer solar system objects' },
              { label: 'Rotation',         value: 'About 7.3 hours (NASA); 7.937 hours in the light-curve database, which warns it may be 30% out' },
              { label: 'Activity',         value: 'No dust seen' },
              { label: 'Extra push',       value: 'A small acceleration away from the Sun, in JPL\'s orbit after Micheli et al. 2018' }
            ]
          }
        ]
      },
      {
        id: 'path',
        label: 'Its path through the solar system',
        summary: 'A steep, backward-running hyperbola',
        payload: [
          {
            type: 'description',
            text: 'ʻOumuamua fell in from high above the plane of the planets, swung round the Sun on a backward-running path and climbed away again. Its orbit is a hyperbola, open at both ends, so it was never bound to the Sun and will not return. The path drawn in the viewer is JPL Horizons\' from 1800 to 2200.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Perihelion distance', value: '0.256 AU (38.3 million km)' },
              { label: 'Eccentricity',        value: '1.2011' },
              { label: 'Inclination',         value: '122.7° (retrograde)' },
              { label: 'Speed far from the Sun', value: '26.4 km/s (from JPL\'s semi-major axis, -1.272 AU)' },
              { label: 'Heading in from',     value: 'RA 279.5°, Dec +33.9°, in Lyra' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: "NASA: ʻOumuamua", url: 'https://science.nasa.gov/solar-system/comets/oumuamua/' },
              { label: 'JPL Small-Body Database: 1I/ʻOumuamua', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=1I' },
              { label: 'JPL Horizons (path, Earth approach and distance)', url: 'https://ssd.jpl.nasa.gov/horizons/' }
            ]
          }
        ]
      }
    ]
  },
  '3i-atlas': {
    id: '3i-atlas',
    label: '3I/ATLAS',
    type: 'Interstellar comet (C/2025 N1)',
    summary: 'The third object known to have come from another star, a comet found by ATLAS on 1 July 2025.',
    payload: [
      {
        type: 'description',
        text: 'Comet 3I/ATLAS is the third known interstellar object, a comet on a path that clearly began outside the solar system. The NASA-funded ATLAS survey telescope in Rio Hurtado, Chile, first reported it on 1 July 2025, and earlier images of it go back to 14 June. It passed the Sun on 29 October 2025 at 203 million km and is now heading back out to interstellar space.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Designation',       value: '3I/ATLAS (C/2025 N1)' },
          { label: 'Nucleus',           value: 'Between 440 m and 5.6 km across (Hubble, August 2025)' },
          { label: 'Discovered',        value: '1 July 2025, ATLAS, Rio Hurtado, Chile' },
          { label: 'Perihelion',        value: '29 October 2025, 1.36 AU (203 million km)' },
          { label: 'Eccentricity',      value: '6.12 (unbound)' },
          { label: 'Speed',             value: 'About 250,000 km/h at perihelion, 58 km/s far out' },
          { label: 'Came from',         value: 'The direction of Sagittarius' },
          { label: 'Distance from Sun (as of 3 November 2026)', value: '13 AU, outbound' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Found by ATLAS',
        summary: 'A survey that watches for asteroids headed for Earth',
        payload: [
          {
            type: 'description',
            text: 'ATLAS, the Asteroid Terrestrial-impact Last Alert System, sent its first observations of the comet to the Minor Planet Center on 1 July 2025, from its telescope at Rio Hurtado in Chile. Searches of older images found it as far back as 14 June 2025. Its hyperbolic orbit shows that it came from outside the solar system, and the "3I" in its name marks it as the third interstellar object known.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'First reported',    value: '1 July 2025, to the Minor Planet Center' },
              { label: 'Earliest images',   value: '14 June 2025' },
              { label: 'Comet designation', value: 'C/2025 N1 (ATLAS)' }
            ]
          }
        ]
      },
      {
        id: 'path',
        label: 'Through the inner solar system',
        summary: 'Past Mars, round the Sun and away',
        payload: [
          {
            type: 'stats',
            entries: [
              { label: 'Closest to Mars',    value: '3 October 2025, 29 million km' },
              { label: 'Perihelion',         value: '29 October 2025, 203 million km (1.356 AU)' },
              { label: 'Closest to Earth',   value: '19 December 2025, about 270 million km (1.80 AU)' },
              { label: 'Speed at perihelion', value: 'About 250,000 km/h, the highest recorded for a solar system visitor (ESA)' },
              { label: 'Inclination',        value: '175° (backward, close to the plane of the planets)' },
              { label: 'Heading in from',    value: 'RA 295.1°, Dec -19.1°, in Sagittarius' }
            ]
          },
          {
            type: 'description',
            text: 'Its orbit is far more open than ʻOumuamua\'s, with an eccentricity above 6, and it runs backwards, almost in the plane of the planets. It never came closer to the Earth than 1.8 AU. The path drawn in the viewer is JPL Horizons\' from 1800 to 2200.'
          }
        ]
      },
      {
        id: 'nucleus',
        label: 'The comet',
        summary: 'An icy nucleus inside a coma',
        payload: [
          {
            type: 'description',
            text: 'As a comet, 3I/ATLAS has a nucleus that gives off gas and dust as the Sun warms it, wrapping it in a coma. Hubble observations up to 20 August 2025 put the nucleus at no more than 5.6 km across and possibly as small as 440 m. ESA describes it as billions of years old.'
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'No picture resolves the nucleus, so the one drawn is a stand-in. The coma and tails are drawn with the viewer\'s comet code and their size and shape are illustrative: they are brightest near the Sun and fade away between 3.9 AU from it, about where Hubble saw dust streaming off it in July 2025, and 7.7 AU.'
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'Leaving, about 13 AU out',
        payload: [
          {
            type: 'description',
            text: 'On 3 November 2026 3I/ATLAS was about 13 AU from the Sun and still slowing toward the 58 km/s it will keep once it is far from the Sun. JPL\'s latest orbit for it (solution 55, 21 October 2026) is marked temporary, made to support Webb observations of the comet.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Distance from Sun', value: 'About 13 AU (3 November 2026)' },
              { label: 'Orbit solution',    value: 'JPL 55, 21 October 2026, temporary' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Comet 3I/ATLAS', url: 'https://science.nasa.gov/solar-system/comets/3i-atlas/' },
              { label: 'ESA: Comet 3I/ATLAS frequently asked questions', url: 'https://www.esa.int/Science_Exploration/Space_Science/Comet_3I_ATLAS_frequently_asked_questions' },
              { label: 'JPL Small-Body Database: C/2025 N1', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=C%2F2025%20N1' },
              { label: 'JPL Horizons (path, Earth approach and distance)', url: 'https://ssd.jpl.nasa.gov/horizons/' }
            ]
          }
        ]
      }
    ]
  }
};

// Dwarf planets, Sedna and the candidate 2026 ED138 beyond Neptune.
const OUTER_DWARFS = {
  'eris': {
    id: 'eris',
    label: 'Eris',
    type: 'Dwarf planet, scattered Kuiper belt',
    summary: "Pluto's near twin in size, on a steep, stretched orbit, whose discovery helped trigger the 2006 planet definition.",
    payload: [
      {
        type: 'description',
        text: "Eris is a dwarf planet about the same size as Pluto, on a long orbit tilted 44° to the planets that carries it between 38 and 98 AU from the Sun. It was found in images taken at Palomar Observatory on 21 October 2003, and its discovery helped trigger the debate that led the IAU to define a planet in 2006. A stellar occultation in 2010 measured it at 2,326 km across and found a very bright surface and no detectable atmosphere. It has one known moon, Dysnomia, and Eris turns once on its axis for each orbit the moon makes."
      },
      {
        type: 'callout',
        tone: 'info',
        text: "The surface shown here is an artistic rendition. Eris is too small and too far away for any telescope to resolve, so its terrain is unknown. The map follows what has been measured: a bright, almost white frost of nitrogen and methane ice with a faint cream tint, and a surface so even that its brightness barely changes as it turns."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: '2,326 km' },
          { label: 'Mass',              value: '1.647 × 10²² kg (Eris and Dysnomia together)' },
          { label: 'Distance from Sun', value: '38.1 to 97.6 AU (mean 67.8 AU)' },
          { label: 'Orbital period',    value: '558 Earth years' },
          { label: 'Day length',        value: 'About 15.8 days (locked to Dysnomia)' },
          { label: 'Moons',             value: '1 (Dysnomia)' },
          { label: 'Discovered',        value: 'Images of 21 October 2003, Palomar Observatory' },
          { label: 'Distance now',      value: '95.5 AU from the Sun (as of 10 October 2026)' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Discovery and the planet debate',
        summary: 'Found in 2005, larger than Pluto by first estimates, one of the first three dwarf planets',
        payload: [
          {
            type: 'description',
            text: "Mike Brown (Caltech), Chad Trujillo (Gemini Observatory) and David Rabinowitz (Yale) found Eris on 5 January 2005 in survey images taken at Palomar Observatory on 21 October 2003. Their 2005 paper said it was bright enough to be larger than Pluto for any reasonable albedo. The discovery team nicknamed it Xena, and its provisional designation was 2003 UB313. On 24 August 2006 the IAU General Assembly in Prague defined a planet and created the dwarf planet class, whose first members were Ceres, Pluto and 2003 UB313."
          },
          {
            type: 'list',
            entries: [
              { primary: '21 October 2003', secondary: 'Palomar survey images record Eris' },
              { primary: '5 January 2005',  secondary: 'Brown, Trujillo and Rabinowitz find it in those images' },
              { primary: '10 September 2005', secondary: 'Its moon Dysnomia is discovered with the Keck telescope on Mauna Kea' },
              { primary: '24 August 2006',  secondary: 'IAU Resolution 5A defines planets and dwarf planets' },
              { primary: '13 September 2006', secondary: 'Named (136199) Eris, after the Greek goddess of discord and strife; the moon is named Dysnomia' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Under Resolution 5A a dwarf planet orbits the Sun, is massive enough to pull itself into a nearly round shape, and has not cleared the neighbourhood around its orbit. Resolution 6A, which made Pluto the prototype of the new class, passed with 237 votes in favour, 157 against and 17 abstentions.'
          }
        ]
      },
      {
        id: 'surface',
        label: 'Surface and atmosphere',
        summary: 'Methane ice, a very bright surface, an atmosphere that may lie frozen on the ground',
        payload: [
          {
            type: 'description',
            text: "Infrared spectra from the Gemini Observatory showed in 2005 that Eris is covered in frozen methane, like Pluto, though its visible colour is almost neutral where Pluto is very red. When Eris passed in front of a star on 6 November 2010, observers found no atmosphere with a surface pressure above about 1 nanobar, some 10,000 times thinner than Pluto's. Its bright surface may be an atmosphere that has collapsed and frozen in the cold, which could return as gas when Eris nears perihelion."
          },
          {
            type: 'stats',
            label: 'Surface',
            entries: [
              { label: 'Main ice',            value: 'Methane (CH₄), with signs of nitrogen ice' },
              { label: 'Atmosphere',          value: 'None detected above about 1 nanobar' },
              { label: 'Surface temperature', value: 'About −243 to −217 °C' },
              { label: 'D/H in methane',      value: '2.5 × 10⁻⁴ (JWST)' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "JWST's NIRSpec spectrometer found Eris's surface rich in methane ice, with evidence of nitrogen ice and no carbon monoxide ice. Grundy and colleagues (2024) read the methane's deuterium content as a sign of hot geochemical processes deep inside. A 2025 study by Mousis and colleagues argues the same ratio fits methane inherited from the solar nebula, so the question is open."
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit',
        summary: 'Tilted 44°, now near its farthest from the Sun',
        payload: [
          {
            type: 'description',
            text: "Eris follows the kind of orbit typical of a scattered Kuiper belt object, unusual only for its 44° inclination. It was near aphelion when it was discovered and is now heading slowly back towards the Sun, which it will next pass closest to in December 2257. At its average distance sunlight takes more than nine hours to reach it."
          },
          {
            type: 'stats',
            label: 'Orbit (JPL Horizons)',
            entries: [
              { label: 'Semi-major axis', value: '67.84 AU' },
              { label: 'Perihelion',      value: '38.09 AU' },
              { label: 'Aphelion',        value: '97.58 AU' },
              { label: 'Eccentricity',    value: '0.438' },
              { label: 'Inclination',     value: '43.99°' },
              { label: 'Orbital period',  value: '558.3 years' },
              { label: 'Next perihelion', value: 'December 2257' },
              { label: 'Distance now',    value: '95.5 AU, moving inwards (as of 10 October 2026)' }
            ]
          }
        ]
      },
      {
        id: 'dysnomia',
        label: 'Dysnomia',
        summary: 'A large moon that weighs the system and has locked Eris into a slow spin',
        payload: [
          {
            type: 'description',
            text: "Dysnomia was discovered on 10 September 2005 by Mike Brown and the Keck adaptive optics team, and is named for the daughter of Eris, the spirit of lawlessness. Hubble images from 2018 give it a nearly circular, prograde orbit of 15.79 days at 37,273 km from Eris. Its orbit gives the combined mass of the pair, and thermal measurements suggest Dysnomia is around 700 km across. Light curves from 2023 show Eris rotates once every 15.8 days, in step with Dysnomia's orbit, so Eris is tidally locked to its moon."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Diameter',          value: 'About 700 km (radius 350 ± 58 km)' },
              { label: 'Orbital period',    value: '15.785899 days' },
              { label: 'Distance from Eris', value: '37,273 km' },
              { label: 'Eccentricity',      value: '0.0062' },
              { label: 'System density',    value: '2.43 g/cm³' },
              { label: 'Next mutual events', value: '2239, when Eris and Dysnomia take turns eclipsing each other' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Eris', url: 'https://science.nasa.gov/dwarf-planets/eris/' },
              { label: 'JPL Small-Body Database: 136199 Eris', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=136199' },
              { label: 'IAU: IAU names dwarf planet Eris (2006)', url: 'https://www.iau.org/Iau/News/PR2006/iau-names-dwarf-planet-eris.aspx' },
              { label: 'IAU: Result of the 2006 resolution votes', url: 'https://www.iau.org/Iau/News/PR2006/iau-2006-general-assembly-resolution-votes.aspx' },
              { label: 'IAU Gazetteer of Planetary Nomenclature: discoverers', url: 'https://planetarynames.wr.usgs.gov/Page/Planets' },
              { label: 'JPL Horizons (orbit about the solar system\'s centre of mass on 9 June 2026, distance on 10 October 2026)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'Brown, Trujillo and Rabinowitz 2005, ApJ (discovery)', url: 'https://doi.org/10.1086/499336' },
              { label: 'Sicardy et al. 2011, Nature (occultation)', url: 'https://www.nature.com/articles/nature10550' },
              { label: 'Holler et al. 2021, Icarus (orbit of Dysnomia)', url: 'https://arxiv.org/abs/2009.13733' },
              { label: 'Szakáts et al. 2023, A&A (tidally locked rotation)', url: 'https://doi.org/10.1051/0004-6361/202245234' },
              { label: 'Bernstein et al. 2023, PSJ (synchronous rotation)', url: 'https://doi.org/10.3847/psj/acdd5f' },
              { label: 'Grundy et al. 2024, Icarus (JWST methane)', url: 'https://doi.org/10.1016/j.icarus.2023.115923' },
              { label: 'Mousis et al. 2025, ApJ Letters (primordial methane)', url: 'https://doi.org/10.3847/2041-8213/adc134' }
            ]
          }
        ]
      }
    ]
  },

  'makemake': {
    id: 'makemake',
    label: 'Makemake',
    type: 'Dwarf planet, Kuiper belt',
    summary: 'A bright, reddish, methane-covered dwarf planet, the second brightest object in the Kuiper belt after Pluto.',
    payload: [
      {
        type: 'description',
        text: "Makemake is a dwarf planet in the Kuiper belt, about 1,430 km across, found at Palomar Observatory on 31 March 2005 by Mike Brown, Chad Trujillo and David Rabinowitz. Its reddish surface is covered in methane ice with ethane and other hydrocarbons, and is brighter than Pluto's, with a geometric albedo of 0.77. The IAU named it after the creator god of Rapa Nui (Easter Island) and classed it as the fourth dwarf planet on 19 July 2008. Hubble images from 2015 revealed a small, dark moon."
      },
      {
        type: 'callout',
        tone: 'info',
        text: "The surface shown here is an artistic rendition. No image has resolved Makemake, so its terrain is unknown. The map follows what has been measured: pale tan methane ice, which may lie in large glassy slabs, and a few small dark patches, since its heat points to some dark ground and its brightness hardly changes as it turns."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Size',              value: '1,430 × 1,502 km (projected outline, 2011)' },
          { label: 'Density',           value: 'About 1.7 g/cm³ (estimate)' },
          { label: 'Distance from Sun', value: '38.2 to 52.8 AU (mean 45.5 AU)' },
          { label: 'Orbital period',    value: '307 Earth years' },
          { label: 'Day length',        value: 'About 22.8 hours (uncertain)' },
          { label: 'Moons',             value: '1 (S/2015 (136472) 1, nicknamed MK 2)' },
          { label: 'Discovered',        value: '31 March 2005, Palomar Observatory' },
          { label: 'Distance now',      value: '52.7 AU from the Sun (as of 10 October 2026)' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Discovery and naming',
        summary: 'Nicknamed Easterbunny, named for the Rapa Nui creator god in 2008',
        payload: [
          {
            type: 'description',
            text: "Makemake was found a few days after Easter 2005, so its discoverers nicknamed it Easterbunny; its provisional designation was 2005 FY9. With Eris and 2003 EL61 (now Haumea), it was one of the discoveries that prompted the IAU to reconsider the definition of a planet. In June 2008 the IAU adopted the word plutoid for dwarf planets beyond Neptune, and on 19 July 2008 it named Makemake and classed it as the fourth dwarf planet and the third plutoid."
          },
          {
            type: 'list',
            entries: [
              { primary: '31 March 2005', secondary: 'Discovered at Palomar by Brown, Trujillo and Rabinowitz' },
              { primary: '11 June 2008',  secondary: 'IAU adopts "plutoid" for dwarf planets beyond Neptune' },
              { primary: '19 July 2008',  secondary: 'Named Makemake, the fourth dwarf planet and third plutoid' },
              { primary: '27 April 2015', secondary: 'Hubble images reveal its moon' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Makemake is the creator of humanity and the god of fertility in the mythology of Rapa Nui. He was the chief god of the Tangata manu bird-man cult, worshipped in the form of sea birds. At its brightest Makemake reaches about magnitude 16.5, within reach of a high-end amateur telescope.'
          }
        ]
      },
      {
        id: 'surface',
        label: 'Surface and a trace of gas',
        summary: 'Almost pure methane ice, hydrocarbons, and methane gas seen by JWST',
        payload: [
          {
            type: 'description',
            text: "Makemake is reddish, and its surface is covered with large amounts of almost pure methane ice. A stellar occultation on 23 April 2011 gave a geometric albedo of 0.77, higher than Pluto's and lower than Eris's, and found no global atmosphere above 4 to 12 nanobar. In 2025 JWST spectra showed methane, deuterated methane, acetylene and ethane ices, and the first sign of methane gas glowing in sunlight, making Makemake the second trans-Neptunian object after Pluto with confirmed release of volatiles."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Methane (CH₄)',          secondary: 'The dominant ice' },
              { primary: 'Deuterated methane (CH₃D)', secondary: 'Gives a D/H ratio of 3.98 × 10⁻⁴' },
              { primary: 'Ethane and acetylene',   secondary: 'Products of methane broken up by sunlight, so the break-up is ongoing or recent' },
              { primary: 'Methane gas',            secondary: 'Fluorescence seen by JWST, from a thin atmosphere or a local plume' }
            ]
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit',
        summary: 'A moderately tilted Kuiper belt orbit, past perihelion and heading outwards',
        payload: [
          {
            type: 'description',
            text: 'Makemake last passed perihelion in July 1880 and is now moving slowly outwards. At its average distance sunlight takes about 6 hours 20 minutes to reach it.'
          },
          {
            type: 'stats',
            label: 'Orbit (JPL Horizons)',
            entries: [
              { label: 'Semi-major axis', value: '45.50 AU' },
              { label: 'Perihelion',      value: '38.20 AU' },
              { label: 'Aphelion',        value: '52.80 AU' },
              { label: 'Eccentricity',    value: '0.160' },
              { label: 'Inclination',     value: '29.00°' },
              { label: 'Orbital period',  value: '306.7 years' },
              { label: 'Last perihelion', value: 'July 1880' },
              { label: 'Distance now',    value: '52.7 AU, moving outwards (as of 10 October 2026)' }
            ]
          }
        ]
      },
      {
        id: 'moon',
        label: 'Its moon, MK 2',
        summary: 'A small, dark moon hidden in the glare until 2015',
        payload: [
          {
            type: 'description',
            text: "The moon S/2015 (136472) 1, nicknamed MK 2, turned up in Hubble Wide Field Camera 3 images taken on 27 April 2015, 7.80 magnitudes fainter than Makemake. Its orbit is seen nearly edge-on, which keeps it lost in Makemake's glare for much of the time. If its surface reflects 4% of sunlight it is about 175 km across. Its discoverers suggest much of the dark material seen in thermal measurements of the system may lie on this moon, and tracking its orbit will give Makemake's mass."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Brightness',        value: '7.80 magnitudes fainter than Makemake' },
              { label: 'Diameter',          value: 'About 175 km (at 4% albedo)' },
              { label: 'Semi-major axis',   value: 'At least about 21,000 km (if circular)' },
              { label: 'Orbital period',    value: 'Longer than 12.4 days (if circular)' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Makemake', url: 'https://science.nasa.gov/dwarf-planets/makemake/' },
              { label: 'JPL Small-Body Database: 136472 Makemake', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=136472' },
              { label: 'IAU: Fourth dwarf planet named Makemake (2008)', url: 'https://iauarchive.eso.org/news/pressreleases/detail/iau0806/' },
              { label: 'IAU: Plutoid chosen as name for objects like Pluto (2008)', url: 'https://iauarchive.eso.org/news/pressreleases/detail/iau0804/' },
              { label: 'IAU: Result of the 2006 resolution votes', url: 'https://www.iau.org/Iau/News/PR2006/iau-2006-general-assembly-resolution-votes.aspx' },
              { label: 'IAU Gazetteer of Planetary Nomenclature: discoverers', url: 'https://planetarynames.wr.usgs.gov/Page/Planets' },
              { label: 'JPL Horizons (orbit about the solar system\'s centre of mass on 9 June 2026, distance on 10 October 2026)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'Ortiz et al. 2012, Nature (occultation)', url: 'https://www.nature.com/articles/nature11597' },
              { label: 'Parker et al. 2016, ApJ Letters (discovery of the moon)', url: 'https://doi.org/10.3847/2041-8205/825/1/L9' },
              { label: 'Protopapa et al. 2025, ApJ Letters (JWST ices and methane gas)', url: 'https://doi.org/10.3847/2041-8213/adfe63' }
            ]
          }
        ]
      }
    ]
  },

  'haumea': {
    id: 'haumea',
    label: 'Haumea',
    type: 'Dwarf planet, Kuiper belt',
    summary: 'A fast-spinning, elongated dwarf planet with two moons and a ring.',
    payload: [
      {
        type: 'description',
        text: "Haumea is one of the fastest-rotating large objects in the solar system, turning once every 3.9 hours, and the spin has drawn it out into an elongated shape at least 2,322 km long. It is made mostly of rock with a coat of ice, and has two moons, Hi'iaka and Namaka, and a ring announced in 2017, the first found around a Kuiper belt object. The IAU recognised it as the fifth dwarf planet on 17 September 2008 and named it after the Hawaiian goddess of childbirth and fertility."
      },
      {
        type: 'callout',
        tone: 'info',
        text: "The surface shown here is an artistic rendition. Haumea has never been resolved, so the map follows its light: clean crystalline water ice almost everywhere, and one darker, redder region on the equator, the Dark Red Spot found in its light curves in 2008. Its shape is the measured one, the three-axis ellipsoid fitted after Haumea passed in front of a star in 2017."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Size',              value: 'At least 2,322 km along its longest axis' },
          { label: 'Mass',              value: '4.006 × 10²¹ kg' },
          { label: 'Distance from Sun', value: '34.7 to 51.5 AU (mean 43.1 AU)' },
          { label: 'Orbital period',    value: '283 Earth years' },
          { label: 'Day length',        value: '3.915 hours' },
          { label: 'Moons',             value: "2 (Hi'iaka, Namaka)" },
          { label: 'Ring',              value: '1, about 2,287 km from the centre' },
          { label: 'Discovered',        value: '7 March 2003, Sierra Nevada Observatory, Spain' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'spin-and-shape',
        label: 'Spin and shape',
        summary: 'A four-hour day, stretched into a plump cigar',
        payload: [
          {
            type: 'description',
            text: "The IAU described Haumea as shaped like a plump cigar, with a diameter roughly that of Pluto but much thinner. When it passed in front of a star on 21 January 2017, telescopes at several observatories timed an outline 1,704 by 1,138 km, and with its light curve that gives a three-axis shape whose longest axis is at least 2,322 km. That size caps its density at 1,885 kg/m³, closer to Pluto's than earlier estimates, and its shape does not match a uniform body in hydrostatic equilibrium. The same event found no global nitrogen or methane atmosphere."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Rotation period',  value: '3.9154 hours' },
              { label: 'Projected outline', value: '1,704 × 1,138 km (21 January 2017)' },
              { label: 'Longest axis',     value: 'At least 2,322 km' },
              { label: 'Density',          value: 'At most 1,885 kg/m³' },
              { label: 'Geometric albedo', value: '0.51' },
              { label: 'Composition',      value: 'Mostly rock, with a crust of ice' }
            ]
          }
        ]
      },
      {
        id: 'ring',
        label: 'The ring',
        summary: 'The first ring found beyond the Centaurs',
        payload: [
          {
            type: 'description',
            text: "The 2017 occultation also showed brief dips in the star's light on either side of Haumea, from a ring about 70 km wide and 2,287 km from Haumea's centre, with an opacity of 0.5. The ring lies in the plane of Haumea's equator and of Hi'iaka's orbit. Before it, rings were known beyond the giant planets only around the Centaur Chariklo, with something similar around the Centaur Chiron. The result was published in Nature on 12 October 2017."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Radius',  value: 'About 2,287 km' },
              { label: 'Width',   value: 'About 70 km' },
              { label: 'Opacity', value: '0.5' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The ring sits close to the 3:1 resonance with Haumea\'s spin: Haumea rotates three times on its axis in the time a ring particle takes to complete one orbit.'
          }
        ]
      },
      {
        id: 'moons',
        label: "Hi'iaka and Namaka",
        summary: 'Two moons, probably debris from an ancient collision',
        payload: [
          {
            type: 'description',
            text: "Both moons were discovered in 2005 with the Keck telescope on Mauna Kea by Mike Brown and the adaptive optics team. Hi'iaka, the outer and brighter moon, is named for the goddess born from Haumea's mouth, patron of the island of Hawaii and of hula dancers; Namaka, the inner one, is a water spirit born from Haumea's body. Their nearly coplanar orbits support the idea that Haumea suffered a giant collision billions of years ago, and a family of smaller icy bodies on similar orbits, marked by the same deep water-ice spectra, points the same way."
          },
          {
            type: 'list',
            entries: [
              { primary: "Hi'iaka", secondary: 'Found 26 January 2005; semi-major axis 49,880 km; period 49.46 days; mass 1.79 × 10¹⁹ kg' },
              { primary: 'Namaka',  secondary: 'Found 7 November 2005; semi-major axis 25,657 km; period 18.28 days; mass about 1.8 × 10¹⁸ kg (barely measured)' }
            ]
          }
        ]
      },
      {
        id: 'discovery',
        label: 'Discovery and naming',
        summary: 'Two teams claimed it; the IAU names the observatory and no discoverer',
        payload: [
          {
            type: 'description',
            text: "Two teams claim credit for finding Haumea, from observations in 2003 and 2004, and one of them nicknamed it Santa. The IAU's gazetteer gives the discovery date as 7 March 2003 and the place as the Sierra Nevada Observatory in Spain, and lists no discoverer. The discovery was announced in mid-2005 under the provisional designation 2003 EL61. On 17 September 2008 the IAU classed it as the fifth dwarf planet and named it Haumea, the Hawaiian goddess of childbirth and fertility, who also represents the element of stone."
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit',
        summary: 'Inclined 28°, heading in towards perihelion in 2133',
        payload: [
          {
            type: 'stats',
            label: 'Orbit (JPL Horizons)',
            entries: [
              { label: 'Semi-major axis', value: '43.10 AU' },
              { label: 'Perihelion',      value: '34.70 AU' },
              { label: 'Aphelion',        value: '51.51 AU' },
              { label: 'Eccentricity',    value: '0.195' },
              { label: 'Inclination',     value: '28.20°' },
              { label: 'Orbital period',  value: '282.8 years' },
              { label: 'Next perihelion', value: 'June 2133' },
              { label: 'Distance now',    value: '49.8 AU, moving inwards (as of 10 October 2026)' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Haumea', url: 'https://science.nasa.gov/dwarf-planets/haumea/' },
              { label: 'JPL Small-Body Database: 136108 Haumea', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=136108' },
              { label: 'IAU: IAU names fifth dwarf planet Haumea (2008)', url: 'https://iauarchive.eso.org/news/pressreleases/detail/iau0807/' },
              { label: 'IAU Gazetteer of Planetary Nomenclature: discoverers', url: 'https://planetarynames.wr.usgs.gov/Page/Planets' },
              { label: 'JPL Horizons (orbit about the solar system\'s centre of mass on 9 June 2026, distance on 10 October 2026)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'Ortiz et al. 2017, Nature (size, shape and ring)', url: 'https://www.nature.com/articles/nature24051' },
              { label: 'Ragozzine and Brown 2009, AJ (orbits and masses of the moons)', url: 'https://arxiv.org/abs/0903.4213' }
            ]
          }
        ]
      }
    ]
  },

  'sedna': {
    id: 'sedna',
    label: 'Sedna',
    type: 'Detached trans-Neptunian object (sednoid)',
    summary: 'A distant red world that never comes closer than 76 AU to the Sun, a likely member of the inner Oort cloud.',
    payload: [
      {
        type: 'description',
        text: "Sedna is a body about 1,000 km across on an orbit that swings between 76 AU and about 940 AU from the Sun over roughly 11,400 years. Mike Brown, Chad Trujillo and David Rabinowitz found it at Palomar Observatory on 14 November 2003. Astronomers class it as a detached trans-Neptunian object and the first known sednoid, a body whose closest approach to the Sun lies far beyond where Neptune could have scattered it, and it is outside the IAU's list of five recognised dwarf planets. Its discoverers favoured an origin in the cluster of stars where the Sun was born."
      },
      {
        type: 'callout',
        tone: 'info',
        text: "The surface shown here is an artistic rendition. Even Hubble sees Sedna as a single point of light, so its terrain is unknown. The map takes its colour from what has been measured: Sedna is one of the reddest bodies in the solar system, a deep rust tan in true colour, thought to come from organic compounds that radiation builds out of its ices."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: '995 km (Herschel, 2012)' },
          { label: 'Distance from Sun', value: '76.2 to about 937 AU' },
          { label: 'Semi-major axis',   value: '506 AU' },
          { label: 'Orbital period',    value: 'About 11,400 years' },
          { label: 'Next perihelion',   value: 'November 2075' },
          { label: 'Day length',        value: 'About 10.3 hours (uncertain)' },
          { label: 'Discovered',        value: '14 November 2003, Palomar Observatory' },
          { label: 'Distance now',      value: '82.8 AU from the Sun (as of 10 October 2026)' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'classification',
        label: 'What kind of object is it?',
        summary: "A detached object and the first sednoid, outside the IAU's five dwarf planets",
        payload: [
          {
            type: 'description',
            text: "The IAU recognises five dwarf planets: Ceres, Pluto, Haumea, Makemake and Eris. Sedna is not on that list, though some research papers call it a dwarf planet. Its discoverers called it a candidate inner Oort cloud planetoid, on an orbit that stays well beyond the Kuiper belt, and the Herschel team calls it a prominent member of the detached trans-Neptunian population. In 2014 Chad Trujillo and Scott Sheppard reported a second Sedna-like body, 2012 VP113, with a perihelion of 80 AU, and suggested that both may belong to the inner Oort cloud."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Sedna',                     secondary: 'Perihelion 76 AU, found 2003' },
              { primary: '2012 VP113',                secondary: 'Perihelion 80 AU, reported 2014' },
              { primary: 'Leleakuhonua (2015 TG387)', secondary: 'The third of the clearest sednoids' }
            ]
          }
        ]
      },
      {
        id: 'discovery',
        label: 'Discovery',
        summary: 'In 2004 the most distant object ever seen in the solar system',
        payload: [
          {
            type: 'description',
            text: "Brown, Trujillo and Rabinowitz found Sedna on 14 November 2003 in the Palomar survey, with the provisional designation 2003 VB12. When they reported it in 2004 it was the most distant object ever seen in the solar system. Pre-discovery images from 2001, 2002 and 2003 pinned down an orbit with a semi-major axis of 480 ± 40 AU and a perihelion of 76 ± 4 AU. It is named for the Inuit goddess of the sea and mother of all sea creatures."
          }
        ]
      },
      {
        id: 'orbit',
        label: 'An orbit no planet explains',
        summary: 'A perihelion of 76 AU, far beyond the reach of Neptune',
        payload: [
          {
            type: 'description',
            text: "Neptune can push the perihelion of a scattered object out to only about 36 AU, or about 50 AU with planetary migration, so none of the major planets could have put Sedna on its orbit. Its discoverers proposed three explanations: an unseen planet, a close pass by another star, or the Sun's birth in a cluster of stars. A 2024 study ran the three clearest sednoids back through the age of the solar system and found the long axes of their orbits lined up only 4.5 billion years ago; if more sednoids follow the pattern, that points to an event in the era of planet formation. Sedna is now heading in towards perihelion in 2076."
          },
          {
            type: 'stats',
            label: 'Orbit (JPL Horizons)',
            entries: [
              { label: 'Semi-major axis', value: '506.4 AU' },
              { label: 'Perihelion',      value: '76.19 AU' },
              { label: 'Aphelion',        value: '937 AU' },
              { label: 'Eccentricity',    value: '0.850' },
              { label: 'Inclination',     value: '11.93°' },
              { label: 'Orbital period',  value: 'About 11,390 years' },
              { label: 'Next perihelion', value: 'November 2075' },
              { label: 'Distance now',    value: '82.8 AU, moving inwards (as of 10 October 2026)' }
            ]
          }
        ]
      },
      {
        id: 'surface',
        label: 'Surface',
        summary: 'Red, bright for its size, rich in ethane and other hydrocarbons',
        payload: [
          {
            type: 'description',
            text: "Herschel's thermal measurements give Sedna a diameter of 995 ± 80 km and a geometric albedo of 0.32, brighter and smaller than expected, so more of its surface may be ice. JWST's NIRSpec spectrometer found ethane, acetylene and ethylene ices, water ice and possibly some carbon dioxide, on a surface with a steep red slope. These light hydrocarbons come from methane broken up by radiation, and their continued presence implies the methane is being resupplied, which suggests Sedna's interior has melted and evolved like those of the larger dwarf planets."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Ethane (C₂H₆)',    secondary: 'Many absorption features' },
              { primary: 'Acetylene (C₂H₂) and ethylene (C₂H₄)', secondary: 'Further products of irradiated methane' },
              { primary: 'Water ice (H₂O)',  secondary: 'Detected' },
              { primary: 'Carbon dioxide (CO₂)', secondary: 'Possibly, in minor amounts' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'JPL Small-Body Database: 90377 Sedna', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=90377' },
              { label: 'Brown, Trujillo and Rabinowitz 2004, ApJ (discovery)', url: 'https://arxiv.org/abs/astro-ph/0404456' },
              { label: 'NASA Science: dwarf planets', url: 'https://science.nasa.gov/dwarf-planets/' },
              { label: 'JPL Horizons (orbit about the solar system\'s centre of mass on 9 June 2026, distance on 10 October 2026)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'Pál et al. 2012, A&A (Herschel size and albedo)', url: 'https://arxiv.org/abs/1204.0899' },
              { label: 'Trujillo and Sheppard 2014, Nature (2012 VP113)', url: 'https://www.nature.com/articles/nature13156' },
              { label: 'Huang and Gladman 2024, ApJ Letters (sednoid alignment)', url: 'https://doi.org/10.3847/2041-8213/ad2686' },
              { label: 'Emery et al. 2024, Icarus (JWST spectroscopy)', url: 'https://doi.org/10.1016/j.icarus.2024.116017' }
            ]
          }
        ]
      }
    ]
  },

  // Announced 27 October 2026. Orbit from JPL's solution of 29 October
  // (epoch JD 2461200.5); distance and brightness from Horizons for 30
  // October; the rest from NOIRLab's release and MPEC 2026-U85.
  '2026 ed138': {
    id: '2026 ed138',
    label: '2026 ED138',
    type: 'Dwarf planet candidate, classical Kuiper belt',
    summary: "A large icy world a little farther out than Pluto on average, found by Rubin Observatory in 2026 and still waiting to be confirmed as a dwarf planet.",
    payload: [
      {
        type: 'description',
        text: "2026 ED138 is a large, bright trans-Neptunian object and the first dwarf planet candidate found by Rubin Observatory, in images taken from March to July 2026 as its ten-year sky survey began. It is about 47 AU from the Sun, beyond Pluto's average distance, on a 283-year orbit tilted almost 25° to the planets. From its brightness and distance it is estimated at 600 to 1,500 km across, and icy bodies that large are expected to be round, though infrared observations are still needed to pin down its size and confirm it as a dwarf planet."
      },
      {
        type: 'callout',
        tone: 'info',
        text: "The surface shown here is an artistic rendition. In every image so far ED138 is a single point of light, so its real colours and terrain are unknown. This map is modelled on the large icy worlds New Horizons saw up close, Pluto and Charon."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: '600 to 1,500 km (estimate)' },
          { label: 'Distance from Sun', value: '37.1 to 49.1 AU (mean 43.1 AU)' },
          { label: 'Orbital period',    value: '283 Earth years' },
          { label: 'Day length',        value: 'Not measured yet' },
          { label: 'Moons',             value: 'None known' },
          { label: 'Discovered',        value: 'Found 13 October 2026 in Rubin images from 4 March' },
          { label: 'Distance now',      value: '47.2 AU from the Sun (as of 30 October 2026)' },
          { label: 'Surface shown',     value: 'Artistic rendition' }
        ]
      }
    ],
    children: [
      {
        id: 'discovery',
        label: 'Discovery',
        summary: 'Found in Rubin data on 13 October 2026, then traced back to 1937',
        payload: [
          {
            type: 'description',
            text: "Matthew Holman of the Center for Astrophysics | Harvard & Smithsonian found ED138 on the evening of 13 October 2026. He was running data Rubin had collected earlier in the year through a search code he developed with Kevin Napier, which compares billions of detections of moving objects and links the ones that belong to the same body. Its brightness and distance told him at once that it was unusually large. Mario Jurić, Rubin's Solar System lead scientist, then found 20 more Rubin observations going back to 4 March. In all, Rubin's Simonyi Survey Telescope recorded ED138 30 times on 20 nights between 4 March and 11 July 2026, and archive images from the Dark Energy Camera, found by the Asteroid Institute's ADAM Precovery service, carry its orbit back to 2016. Since the announcement older survey plates have been searched too, and JPL's orbit of 29 October already fits positions measured on photographs from Palomar in 1954 and from Boyden Observatory in South Africa on 30 December 1937."
          },
          {
            type: 'list',
            entries: [
              { primary: '30 December 1937', secondary: 'The oldest known image, a photographic plate from Boyden Observatory, found after the announcement' },
              { primary: '25 February 2016', secondary: 'The earliest Dark Energy Camera images in the announced orbit' },
              { primary: '4 March 2026',     secondary: 'First Rubin observation, which gives the E in its name (the first half of March)' },
              { primary: '11 July 2026',     secondary: 'Last of 30 Rubin observations on 20 nights' },
              { primary: '13 October 2026',  secondary: 'Holman finds it in the data' },
              { primary: '27 October 2026',  secondary: 'The Minor Planet Center announces it in MPEC 2026-U85, and the team presents it at the Division for Planetary Sciences meeting in Spokane' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "ED138 lies in Corona Australis, close to the plane of the Milky Way, a crowded field of millions of stars. That is probably why earlier surveys missed it. Rubin images the whole southern sky again and again, and its software picks out the few points of light that move."
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit',
        summary: 'A 283-year orbit tilted almost 25°, stirred up by Neptune',
        payload: [
          {
            type: 'description',
            text: "ED138 takes about 283 years to go round the Sun, on a mildly stretched orbit between 37.1 and 49.1 AU tilted 24.4° to the plane of the planets. That tilt makes it a hot classical trans-Neptunian object, one thought to have been pushed off its original orbit by Neptune's gravity. It last passed closest to the Sun in 1926, is now heading outwards, and will be at its farthest late in the 2060s. At its present distance sunlight takes about six and a half hours to reach it."
          },
          {
            type: 'stats',
            label: 'Orbit (JPL Horizons)',
            entries: [
              { label: 'Semi-major axis', value: '43.09 AU' },
              { label: 'Perihelion',      value: '37.11 AU' },
              { label: 'Aphelion',        value: '49.07 AU' },
              { label: 'Eccentricity',    value: '0.139' },
              { label: 'Inclination',     value: '24.43°' },
              { label: 'Orbital period',  value: '282.7 years' },
              { label: 'Last perihelion', value: '1926' },
              { label: 'Distance now',    value: '47.2 AU, moving outwards (as of 30 October 2026)' }
            ]
          }
        ]
      },
      {
        id: 'size',
        label: 'Size and confirmation',
        summary: 'Somewhere between 600 and 1,500 km, waiting on infrared observations',
        payload: [
          {
            type: 'description',
            text: "No image yet resolves ED138's disc, so its size is worked out from how bright it looks at its distance. That depends on how much sunlight its surface reflects, which nobody has measured yet: a dark surface would make it about 1,500 km across, a bright one about 600 km. Icy bodies in that range are expected to have pulled themselves round, the main test for a dwarf planet. Observations in the infrared are needed to pin down its size and confirm what it is."
          },
          {
            type: 'stats',
            label: 'Brightness',
            entries: [
              { label: 'Absolute magnitude (H)', value: '2.93 (MPC)' },
              { label: 'In the sky now',         value: 'About magnitude 19.8, in Corona Australis' },
              { label: 'Diameter estimate',      value: '600 to 1,500 km' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'If it is confirmed, ED138 would join the five dwarf planets the IAU recognises: Pluto, Ceres, Eris, Haumea and Makemake.'
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NOIRLab: Rubin Observatory reveals its first dwarf planet candidate', url: 'https://noirlab.edu/public/news/noirlab2626/' },
              { label: 'Minor Planet Center: MPEC 2026-U85', url: 'https://www.minorplanetcenter.net/mpec/K26/K26U85.html' },
              { label: 'Minor Planet Center: 2026 ED138 and its observations', url: 'https://minorplanetcenter.net/db_search/show_object?object_id=2026+ED138' },
              { label: 'JPL Small-Body Database: 2026 ED138', url: 'https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=2026%20ED138' },
              { label: 'JPL Horizons (orbit about the solar system\'s centre of mass on 9 June 2026, distance and brightness on 30 October 2026)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'Minor Planet Center: how provisional designations work', url: 'https://www.minorplanetcenter.net/iau/info/OldDesDoc.html' }
            ]
          }
        ]
      }
    ]
  }
};

// Deep-space probes.
const HORIZONS = { label: 'JPL Horizons (positions for 10 October 2026)', url: 'https://ssd.jpl.nasa.gov/horizons/' };
const VOYAGER_WHERE = { label: 'NASA: Where are Voyager 1 and Voyager 2 now?', url: 'https://science.nasa.gov/mission/voyager/where-are-voyager-1-and-voyager-2-now/' };
const VOYAGER_FACTS = { label: 'NASA: Voyager fast facts', url: 'https://science.nasa.gov/mission/voyager/fast-facts/' };
const VOYAGER_2025 = { label: 'NASA: Two Voyager instruments turned off (5 March 2025)', url: 'https://science.nasa.gov/blogs/voyager/2025/03/05/nasa-turns-off-2-voyager-science-instruments-to-extend-mission/' };
const VOYAGER_LECP_2026 = { label: 'NASA: Instrument shut off on Voyager 1 (17 April 2026)', url: 'https://science.nasa.gov/blogs/voyager/2026/04/17/nasa-shuts-off-instrument-on-voyager-1-to-keep-spacecraft-operating/' };
const VOYAGER_BIG_BANG = { label: "NASA: Engineers help prolong Voyager 2's science (4 August 2026)", url: 'https://science.nasa.gov/blogs/voyager/2026/08/04/nasa-engineers-help-prolong-voyager-2s-science-mission/' };

const PROBES = {
  'voyager 1': {
    id: 'voyager 1',
    label: 'Voyager 1',
    type: 'Interstellar probe',
    summary: 'The most distant human-made object, in interstellar space since 2012.',
    payload: [
      {
        type: 'description',
        text: 'Voyager 1 launched on 5 September 1977 to fly past Jupiter and Saturn, and on 25 August 2012 it became the first spacecraft to leave the heliosphere and enter interstellar space. It has been the most distant human-made object since it passed Pioneer 10 on 17 February 1998. As of 17 April 2026 two of its instruments still work, the magnetometer and the plasma wave subsystem.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Launched',          value: '5 September 1977, Titan IIIE-Centaur, Cape Canaveral' },
          { label: 'Mass',              value: '721.9 kg' },
          { label: 'Operator',          value: 'NASA Jet Propulsion Laboratory' },
          { label: 'Distance from Sun', value: '25.73 billion km (172.0 AU)' },
          { label: 'Distance from Earth', value: '25.79 billion km (172.4 AU)' },
          { label: 'Signal time',       value: '23 h 54 min one way' },
          { label: 'Speed',             value: '16.9 km/s away from the Sun' },
          { label: 'Positions as of',   value: '10 October 2026 (JPL Horizons)' },
          { label: 'Status',            value: 'Operating, 2 of 10 instruments on (17 April 2026)' }
        ]
      }
    ],
    children: [
      {
        id: 'mission',
        label: 'The mission',
        summary: 'Jupiter, Saturn and Titan, then out of the plane of the planets',
        payload: [
          {
            type: 'description',
            text: "Voyager began as a Grand Tour of the outer planets, cut back for budget reasons to two spacecraft for Jupiter and Saturn, called Mariner Jupiter/Saturn until about six months before launch. Voyager 1 left 16 days after its twin on a faster path and passed Voyager 2's distance from the Sun on 15 December 1977, while both were in the asteroid belt. Its Saturn flyby was planned around a close look at Titan, so it was never sent on to Uranus or Neptune, and after Saturn it headed north out of the plane of the planets."
          },
          {
            type: 'stats',
            label: 'Encounters (closest approach)',
            entries: [
              { label: 'Jupiter',           value: '5 March 1979, 12:05 UT, 280,000 km' },
              { label: 'Saturn',            value: '12 November 1980, 23:46 UT, 126,000 km' },
              { label: 'Images of Jupiter', value: 'About 18,000, of the planet and its moons' },
              { label: 'Images of Saturn',  value: 'About 16,000, of the planet, rings and moons' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'On 14 February 1990, about 40 AU from the Sun, Voyager 1 looked back and took a mosaic of six planets, the family portrait of the solar system. Its picture of Earth became the Pale Blue Dot. The cameras were then switched off to save power.'
          }
        ]
      },
      {
        id: 'instruments',
        label: 'Instruments',
        summary: 'Ten instruments, two still on',
        payload: [
          {
            type: 'description',
            text: 'Each Voyager carries the same ten science instruments. Some built for the planet flybys were turned off once the giant planets were done, and since then instruments have been switched off one at a time as the power runs down, in an order the science and engineering teams agreed years ago. Three radioisotope thermoelectric generators gave about 470 W at launch, and each Voyager now loses about 4 W a year.'
          },
          {
            type: 'stats',
            label: 'Status (NASA, updated 17 April 2026)',
            entries: [
              { label: 'Magnetometer (MAG)',                  value: 'On' },
              { label: 'Plasma wave subsystem (PWS)',         value: 'On' },
              { label: 'Low-energy charged particles (LECP)', value: 'Off to save power, 17 April 2026' },
              { label: 'Cosmic ray subsystem (CRS)',          value: 'Off to save power, 25 February 2025' },
              { label: 'Ultraviolet spectrometer (UVS)',      value: 'Off to save power, 19 April 2016' },
              { label: 'Planetary radio astronomy (PRA)',     value: 'Off to save power, 15 January 2008' },
              { label: 'Plasma science (PLS)',                value: 'Off, degraded performance, 1 February 2007' },
              { label: 'Infrared spectrometer (IRIS)',        value: 'Off to save power, 3 June 1998' },
              { label: 'Cameras (ISS)',                       value: 'Off to save power, 14 February 1990' },
              { label: 'Photopolarimeter (PPS)',              value: 'Off, degraded performance, 29 January 1980' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'One part of the LECP was left running after the April 2026 shutdown: the 0.5 W motor that turns its sensor. Keeping it on gives the team the best chance of switching the instrument back on if they find spare power.'
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'Almost a light-day from Earth',
        payload: [
          {
            type: 'stats',
            label: 'As of 10 October 2026 (JPL Horizons)',
            entries: [
              { label: 'Distance from Sun',   value: '25.73 billion km (172.0 AU)' },
              { label: 'Distance from Earth', value: '25.79 billion km (172.4 AU)' },
              { label: 'Signal time',         value: '23 h 54 min one way' },
              { label: 'Speed from the Sun',  value: '16.9 km/s' }
            ]
          },
          {
            type: 'description',
            text: 'Voyager 1 is in interstellar space, travelling up and away from the plane of the planets. During a planned roll on 27 February 2026 its power fell unexpectedly, and any further drop could have set off its undervoltage fault protection, so on 17 April 2026 the team shut down the LECP, which engineers expected to buy about a year. As of 4 August 2026 NASA planned to repeat on Voyager 1 the power-saving swap of devices that worked on Voyager 2, nicknamed the Big Bang, in the coming months.'
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'NASA expects Voyager 1 to be one light-day from Earth, 25.902 billion km, on 18 November 2026. It will be the first human-made object to reach that distance.'
          }
        ]
      },
      {
        id: 'discoveries',
        label: 'Key discoveries',
        summary: "Io's volcanoes, new moons and rings, the edge of the heliosphere",
        payload: [
          {
            type: 'list',
            entries: [
              { primary: "Io's volcanoes (1979)",       secondary: "Voyager 1 spotted the first signs of volcanism on Jupiter's moon Io." },
              { primary: "Jupiter's ring (1979)",       secondary: 'A faint ring system around Jupiter, and two new moons, Thebe and Metis.' },
              { primary: "Saturn's G ring (1980)",      secondary: 'Five new moons, a new ring, and shepherd moons that keep some rings well defined.' },
              { primary: "Titan's atmosphere (1980)",   secondary: 'A thick atmosphere that hid the surface completely, found to be 90% nitrogen.' },
              { primary: 'Termination shock (2004)',    secondary: 'On 16 December 2004, at about 94 AU, it reached the termination shock, where the solar wind slows down, and entered the heliosheath.' },
              { primary: 'Interstellar space (2012)',   secondary: 'On 25 August 2012 it became the first spacecraft to exit the heliosphere. Its cosmic ray telescopes helped the team work out when and where.' }
            ]
          }
        ]
      },
      {
        id: 'timeline',
        label: 'Timeline',
        summary: 'From 1977 to one light-day out',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: '5 September 1977', secondary: 'Launch from Cape Canaveral on a Titan IIIE-Centaur' },
              { primary: '15 December 1977', secondary: 'Overtakes Voyager 2 in the asteroid belt' },
              { primary: '5 March 1979',     secondary: 'Closest approach to Jupiter' },
              { primary: '12 November 1980', secondary: 'Closest approach to Saturn' },
              { primary: '1 January 1990',   secondary: 'The Voyager Interstellar Mission begins' },
              { primary: '14 February 1990', secondary: 'Family portrait of the planets; cameras switched off' },
              { primary: '17 February 1998', secondary: 'Passes Pioneer 10 at 69.4 AU to become the most distant human-made object' },
              { primary: '16 December 2004', secondary: 'Crosses the termination shock' },
              { primary: '25 August 2012',   secondary: 'Enters interstellar space' },
              { primary: '25 February 2025', secondary: 'Cosmic ray subsystem switched off' },
              { primary: '17 April 2026',    secondary: 'Low-energy charged particle instrument switched off' },
              { primary: '18 November 2026', secondary: 'Expected to reach one light-day from Earth' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Voyager 1', url: 'https://science.nasa.gov/mission/voyager/voyager-1/' },
              VOYAGER_WHERE,
              { label: 'NSSDCA: Voyager 1', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=1977-084A' },
              VOYAGER_FACTS,
              VOYAGER_LECP_2026,
              VOYAGER_BIG_BANG,
              VOYAGER_2025,
              { label: 'NASA: Io', url: 'https://science.nasa.gov/jupiter/jupiter-moons/io/' },
              { label: 'NASA: Voyager science', url: 'https://science.nasa.gov/mission/voyager/science/' },
              { label: 'NASA: Voyager spacecraft and instruments', url: 'https://science.nasa.gov/mission/voyager/spacecraft/' },
              HORIZONS
            ]
          }
        ]
      }
    ]
  },

  'voyager 2': {
    id: 'voyager 2',
    label: 'Voyager 2',
    type: 'Interstellar probe',
    summary: 'The only spacecraft to visit Uranus and Neptune, in interstellar space since 2018.',
    payload: [
      {
        type: 'description',
        text: 'Voyager 2 launched on 20 August 1977, 16 days before its twin, and is the only spacecraft to have visited Uranus and Neptune. Between 1979 and 1989 it flew past all four giant planets, and on 5 November 2018 it became the second spacecraft to reach interstellar space. As of 4 August 2026 three of its instruments still work, and a power-saving fix made that year should keep them running for at least another year.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Launched',          value: '20 August 1977, Titan IIIE-Centaur, Cape Canaveral' },
          { label: 'Mass',              value: '721.9 kg' },
          { label: 'Operator',          value: 'NASA Jet Propulsion Laboratory' },
          { label: 'Distance from Sun', value: '21.57 billion km (144.2 AU)' },
          { label: 'Distance from Earth', value: '21.56 billion km (144.1 AU)' },
          { label: 'Signal time',       value: '19 h 58 min one way' },
          { label: 'Speed',             value: '15.0 km/s away from the Sun' },
          { label: 'Positions as of',   value: '10 October 2026 (JPL Horizons)' },
          { label: 'Status',            value: 'Operating, 3 of 10 instruments on (4 August 2026)' }
        ]
      }
    ],
    children: [
      {
        id: 'grand-tour',
        label: 'The Grand Tour',
        summary: 'Jupiter, Saturn, Uranus and Neptune in 12 years',
        payload: [
          {
            type: 'description',
            text: "NASA cancelled a Grand Tour of four spacecraft in January 1972, largely over its projected $1 billion cost, and proposed two spacecraft for Jupiter and Saturn instead. In 1974 planners proposed that if the first Voyager succeeded, the second could go on to Uranus and Neptune using gravity assists, and Voyager 2's launch date kept that option open. In April 1978 its primary radio receiver failed and the backup proved faulty, so the rest of the mission has been flown on the backup receiver with workarounds."
          },
          {
            type: 'stats',
            label: 'Encounters (closest approach)',
            entries: [
              { label: 'Jupiter', value: '9 July 1979, 22:29 UT, 645,000 km' },
              { label: 'Saturn',  value: '26 August 1981, 01:21 UT, 101,000 km' },
              { label: 'Uranus',  value: '24 January 1986, 17:59 UT, 81,500 km' },
              { label: 'Neptune', value: '25 August 1989, 03:56 UT, about 4,800 km above the cloud tops' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The Uranus flyby was shaped by the need to reach Neptune afterwards, which left Voyager 2 only 5.5 hours of close study. Neptune was the closest of its four flybys, and all 10 instruments were still working at the time.'
          }
        ]
      },
      {
        id: 'instruments',
        label: 'Instruments',
        summary: 'Ten instruments, three still on',
        payload: [
          {
            type: 'description',
            text: 'Voyager 2 carries the same ten instruments as Voyager 1. In 2018 its plasma science instrument was key to showing that the probe had left the heliosphere, when the flow of plasma into its three Sun-facing cups dropped off sharply. That instrument was switched off on 26 September 2024 and the low-energy charged particle instrument on 24 March 2025, leaving the cosmic ray subsystem, the magnetometer and the plasma wave subsystem.'
          },
          {
            type: 'stats',
            label: 'Status (NASA, updated 17 April 2026)',
            entries: [
              { label: 'Cosmic ray subsystem (CRS)',          value: 'On' },
              { label: 'Magnetometer (MAG)',                  value: 'On' },
              { label: 'Plasma wave subsystem (PWS)',         value: 'On' },
              { label: 'Low-energy charged particles (LECP)', value: 'Off to save power, 24 March 2025' },
              { label: 'Plasma science (PLS)',                value: 'Off to save power, 26 September 2024' },
              { label: 'Planetary radio astronomy (PRA)',     value: 'Off to save power, 21 February 2008' },
              { label: 'Infrared spectrometer (IRIS)',        value: 'Off to save power, 1 February 2007' },
              { label: 'Ultraviolet spectrometer (UVS)',      value: 'Off to save power, 12 November 1998' },
              { label: 'Photopolarimeter (PPS)',              value: 'Off, degraded performance, 3 April 1991' },
              { label: 'Cameras (ISS)',                       value: 'Off to save power, 10 October and 5 December 1989' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The Big Bang (NASA, 4 August 2026): engineers turned off some powered devices and swapped in lower-power ones all at once, while keeping the spacecraft warm enough to work. Without it another instrument would have had to go before the end of 2026. The savings should keep all three running for at least an extra year.'
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'Below the plane of the planets, 20 light-hours out',
        payload: [
          {
            type: 'stats',
            label: 'As of 10 October 2026 (JPL Horizons)',
            entries: [
              { label: 'Distance from Sun',   value: '21.57 billion km (144.2 AU)' },
              { label: 'Distance from Earth', value: '21.56 billion km (144.1 AU)' },
              { label: 'Signal time',         value: '19 h 58 min one way' },
              { label: 'Speed from the Sun',  value: '15.0 km/s' }
            ]
          },
          {
            type: 'description',
            text: 'Voyager 2 is heading away from the Sun beneath the plane of the planets, through interstellar space. Its radioisotope generators lose about 4 W of power a year. In March 2025 engineers said the two Voyagers could have enough electricity to keep at least one science instrument going into the 2030s, though after so long in deep space unforeseen problems could cut that short.'
          }
        ]
      },
      {
        id: 'discoveries',
        label: 'Key discoveries',
        summary: 'Four giant planets, new moons and rings, Triton',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'Jupiter (1979)',            secondary: 'Many changes in the atmosphere, including a drift in the Great Red Spot and changes in its shape and colour, and a 10-hour "volcano watch" on Io.' },
              { primary: "Saturn's rings (1981)",     secondary: 'Detailed images of the ring spokes, the F ring and its shepherd moons. Its data suggested the A ring may be only about 300 m thick.' },
              { primary: 'Uranus (1986)',             secondary: 'Ten new moons, two new rings, and a magnetic field tilted 55 degrees off the axis and off centre. Winds up to 724 km/h.' },
              { primary: 'Neptune (1989)',            secondary: 'Six new moons, four new rings, winds of 1,100 km/h and the Great Dark Spot.' },
              { primary: 'Triton (1989)',             secondary: "Two-thirds of Neptune's largest moon photographed: the coldest known planetary body in the solar system, with a nitrogen ice \"volcano\"." },
              { primary: 'Interstellar space (2018)', secondary: 'Crossed the heliopause on 5 November 2018, the second spacecraft to do so.' }
            ]
          }
        ]
      },
      {
        id: 'timeline',
        label: 'Timeline',
        summary: 'From 1977 to the Big Bang power fix',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: '20 August 1977',    secondary: 'Launch from Cape Canaveral on a Titan IIIE-Centaur' },
              { primary: 'April 1978',        secondary: 'Primary radio receiver fails; the mission continues on the backup' },
              { primary: '9 July 1979',       secondary: 'Closest approach to Jupiter' },
              { primary: '26 August 1981',    secondary: 'Closest approach to Saturn' },
              { primary: '24 January 1986',   secondary: 'First spacecraft to fly past Uranus' },
              { primary: '25 August 1989',    secondary: 'First spacecraft to fly past Neptune' },
              { primary: '30 August 2007',    secondary: 'Crosses the termination shock at about 84 AU' },
              { primary: '5 November 2018',   secondary: 'Enters interstellar space' },
              { primary: '26 September 2024', secondary: 'Plasma science instrument switched off' },
              { primary: '24 March 2025',     secondary: 'Low-energy charged particle instrument switched off' },
              { primary: '4 August 2026',     secondary: 'NASA reports the Big Bang power swap a success' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Voyager 2', url: 'https://science.nasa.gov/mission/voyager/voyager-2/' },
              VOYAGER_WHERE,
              { label: 'NSSDCA: Voyager 2', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=1977-076A' },
              VOYAGER_FACTS,
              VOYAGER_BIG_BANG,
              VOYAGER_2025,
              { label: 'NASA: Plasma science instrument off on Voyager 2 (1 October 2024)', url: 'https://science.nasa.gov/blogs/voyager/2024/10/01/nasa-turns-off-science-instrument-to-save-voyager-2-power/' },
              HORIZONS
            ]
          }
        ]
      }
    ]
  },

  'new horizons': {
    id: 'new horizons',
    label: 'New Horizons',
    type: 'Kuiper Belt probe',
    summary: 'First to Pluto (2015) and Arrokoth (2019), now in the outer Kuiper Belt.',
    payload: [
      {
        type: 'description',
        text: 'New Horizons launched on 19 January 2006 and on 14 July 2015 made the first close flyby of Pluto, passing 12,500 km from the dwarf planet. On 1 January 2019 it flew past Arrokoth, the most distant object ever explored up close. It now measures the charged particles of the outer heliosphere and the dust of the Kuiper Belt, and as of 7 July 2026 it was awake again after its longest hibernation, 321 days.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Launched',          value: '19 January 2006, Atlas V 551 with a Star 48B third stage' },
          { label: 'Launch mass',       value: '465 kg, including 80 kg of propellant' },
          { label: 'Operator',          value: 'Johns Hopkins APL for NASA' },
          { label: 'Distance from Sun', value: '9.84 billion km (65.78 AU)' },
          { label: 'Distance from Earth', value: '9.84 billion km (65.75 AU)' },
          { label: 'Signal time',       value: '9 h 7 min one way' },
          { label: 'Speed',             value: '13.5 km/s away from the Sun' },
          { label: 'Positions as of',   value: '10 October 2026 (JPL Horizons)' },
          { label: 'Status',            value: 'Awake after a 321-day hibernation (NASA, 7 July 2026)' }
        ]
      }
    ],
    children: [
      {
        id: 'mission',
        label: 'The mission',
        summary: 'Jupiter, Pluto and Arrokoth',
        payload: [
          {
            type: 'description',
            text: "New Horizons was the first mission in NASA's New Frontiers programme. Its Star 48B third stage sent it off at about 58,536 km/h, the highest launch speed relative to Earth of any human-made object. A gravity assist at Jupiter on 28 February 2007 added about 14,000 km/h and cut three years off the trip, and it reached Pluto on 14 July 2015 after more than nine years in flight."
          },
          {
            type: 'stats',
            label: 'Encounters (closest approach)',
            entries: [
              { label: 'Jupiter',  value: '28 February 2007, 05:43 UT, 2.3 million km' },
              { label: 'Pluto',    value: '14 July 2015, 11:49 UT, 12,500 km' },
              { label: 'Charon',   value: '14 July 2015, 28,800 km' },
              { label: 'Arrokoth', value: '1 January 2019, 05:33 UT, 3,500 km' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The full Pluto and Charon dataset, about 6.25 gigabytes, took more than 15 months to send home. The spacecraft was about 4.5 light-hours away and could transmit only 1 to 2 kilobits per second, and the last of the data arrived on 25 October 2016.'
          }
        ]
      },
      {
        id: 'instruments',
        label: 'Instruments',
        summary: 'Seven instruments drawing under 28 W',
        payload: [
          {
            type: 'description',
            text: 'The payload has seven instruments: three optical instruments, two plasma instruments, a dust sensor and a radio science receiver. Together they draw less than 28 W. Power comes from a radioisotope thermoelectric generator, a spare from the Cassini mission, which gave about 250 W at launch and about 200 W by the Pluto flyby.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Ralph',  secondary: 'Colour camera (MVIC) and infrared spectrometer (LEISA) that mapped the surfaces and their ices' },
              { primary: 'Alice',  secondary: "Ultraviolet imaging spectrometer that probed Pluto's atmosphere" },
              { primary: 'LORRI',  secondary: 'Long Range Reconnaissance Imager, a 20.8 cm telescope camera that resolved features about 50 m across on Pluto' },
              { primary: 'REX',    secondary: 'Radio science experiment that probed the atmosphere and measured the masses of Pluto and Charon' },
              { primary: 'SWAP',   secondary: 'Solar Wind Around Pluto, a solar wind analyser' },
              { primary: 'PEPSSI', secondary: 'Pluto Energetic Particle Spectrometer Science Investigation, for energetic particles' },
              { primary: 'Venetia Burney Student Dust Counter', secondary: 'Built by students at the University of Colorado Boulder, it counts and sizes dust grains' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'As of 7 July 2026, SWAP, PEPSSI and the Student Dust Counter gather data around the clock, even in hibernation, and Alice was due to look at the hydrogen gas of the outer heliosphere a few weeks after the spacecraft woke.'
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'In the outer Kuiper Belt',
        payload: [
          {
            type: 'stats',
            label: 'As of 10 October 2026 (JPL Horizons)',
            entries: [
              { label: 'Distance from Sun',   value: '9.84 billion km (65.78 AU)' },
              { label: 'Distance from Earth', value: '9.84 billion km (65.75 AU)' },
              { label: 'Signal time',         value: '9 h 7 min one way' },
              { label: 'Speed from the Sun',  value: '13.5 km/s' }
            ]
          },
          {
            type: 'description',
            text: 'New Horizons spent 321 days in hibernation from 7 August 2025, its longest yet, and woke on 23 June 2026 in good health. It runs on updated autonomy software made for working farther from the Sun with less power. In 2023 NASA decided to keep the mission going until the spacecraft leaves the Kuiper Belt, expected in 2028 to 2029, with heliophysics as its focus.'
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'On 1 October 2024 New Horizons passed 60 AU from the Sun, twice as far out as Pluto was during the 2015 flyby.'
          }
        ]
      },
      {
        id: 'discoveries',
        label: 'Key discoveries',
        summary: 'A geologically active Pluto and a two-lobed Arrokoth',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'Jupiter (2007)',               secondary: "The first lightning seen near Jupiter's poles, clumps moving through its faint rings, and the structure inside volcanic eruptions on Io." },
              { primary: "Pluto's size (2015)",           secondary: 'About 2,370 km across, slightly larger than earlier estimates. Charon is about 1,208 km.' },
              { primary: 'Sputnik Planitia',              secondary: "A nitrogen ice glacier about 1,000 km wide in the western half of Pluto's heart, the largest known glacier in the solar system." },
              { primary: 'An active world',               secondary: 'Surface activity, atmospheric haze and a lower escape rate than predicted made scientists revise their models, with hints of an ocean inside Pluto today.' },
              { primary: "Charon's tectonic belt",        secondary: 'An enormous belt of extension faults round its equator, suggesting a water ocean long ago.' },
              { primary: 'Arrokoth (2019)',               secondary: 'A two-lobed body about 35 km long, 20 km wide and 10 km thick. The larger lobe is flattened like two lenses back to back.' },
              { primary: 'Recent liquid on Pluto (2026)', secondary: 'A study published on 31 July 2026 found dark features on northern Sputnik Planitia that point to liquid nitrogen rising through cracks, the first evidence of recently flowing liquid on Pluto.' }
            ]
          }
        ]
      },
      {
        id: 'timeline',
        label: 'Timeline',
        summary: 'From launch to the outer Kuiper Belt',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: '19 January 2006',  secondary: 'Launch from Cape Canaveral on an Atlas V 551' },
              { primary: '7 April 2006',     secondary: 'Passes the orbit of Mars' },
              { primary: '13 June 2006',     secondary: 'Passes asteroid 132524 APL at 101,867 km' },
              { primary: '28 February 2007', secondary: 'Jupiter gravity assist' },
              { primary: '8 June 2008',      secondary: 'Crosses the orbit of Saturn' },
              { primary: '18 March 2011',    secondary: 'Crosses the orbit of Uranus' },
              { primary: '25 August 2014',   secondary: 'Crosses the orbit of Neptune' },
              { primary: '14 July 2015',     secondary: 'First flyby of Pluto and Charon' },
              { primary: '25 October 2016',  secondary: 'The last of the Pluto data reaches Earth' },
              { primary: '1 January 2019',   secondary: 'Flies past Arrokoth' },
              { primary: '1 October 2024',   secondary: 'Passes 60 AU from the Sun' },
              { primary: '7 August 2025',    secondary: 'Begins a 321-day hibernation' },
              { primary: '23 June 2026',     secondary: 'Wakes in good health' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: New Horizons', url: 'https://science.nasa.gov/mission/new-horizons/' },
              { label: 'APL: New Horizons mission timeline', url: 'https://pluto.jhuapl.edu/Mission/The-Path-to-Pluto/Mission-Timeline.php' },
              { label: 'NSSDCA: New Horizons', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=2006-001A' },
              { label: 'APL: New Horizons payload', url: 'https://pluto.jhuapl.edu/Mission/Spacecraft/Payload.php' },
              { label: 'NASA: New Horizons wakes from hibernation (7 July 2026)', url: 'https://science.nasa.gov/missions/new-horizons/nasas-new-horizons-spacecraft-wakes-from-hibernation-in-good-health/' },
              { label: 'NASA: Longest hibernation begins (22 August 2025)', url: 'https://science.nasa.gov/blogs/new-horizons/2025/08/22/nasas-new-horizons-enters-missions-longest-hibernation-period/' },
              { label: 'NASA: New Horizons to continue exploring (29 September 2023)', url: 'https://www.nasa.gov/missions/new-horizons/nasas-new-horizons-to-continue-exploring-outer-solar-system/' },
              { label: "NASA: Evidence of recent liquid on Pluto's surface (August 2026)", url: 'https://science.nasa.gov/blogs/science-news/2026/08/05/nasas-new-horizons-finds-evidence-of-recent-liquid-on-plutos-surface/' },
              HORIZONS
            ]
          }
        ]
      }
    ]
  },

  'pioneer 10': {
    id: 'pioneer 10',
    label: 'Pioneer 10',
    type: 'Interplanetary probe (silent since 2003)',
    summary: 'First through the asteroid belt and first to Jupiter, now drifting towards Aldebaran.',
    payload: [
      {
        type: 'description',
        text: 'Pioneer 10 was NASA\'s first mission to the outer planets, the first spacecraft to cross the asteroid belt and the first to fly past Jupiter, on 4 December 1973. Designed for a 21-month mission, it lasted more than 30 years, and its last signal reached Earth on 23 January 2003 from 12.23 billion km away. Silent now, it is heading generally towards the star Aldebaran, which it should pass in about two million years.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Launched',          value: '2 March 1972 local time (3 March, 01:49 UTC), Atlas-Centaur, Cape Canaveral' },
          { label: 'Mass',              value: '258 kg' },
          { label: 'Operator',          value: 'NASA Ames Research Center (built by TRW)' },
          { label: 'Last signal',       value: '23 January 2003, from 12.23 billion km' },
          { label: 'Distance from Sun', value: 'About 21.24 billion km (142.0 AU), predicted' },
          { label: 'Distance from Earth', value: 'About 21.18 billion km (141.5 AU), predicted' },
          { label: 'Positions as of',   value: '10 October 2026 (JPL Horizons prediction)' },
          { label: 'Status',            value: 'Silent: its power fell too low to run the transmitter' }
        ]
      }
    ],
    children: [
      {
        id: 'mission',
        label: 'The mission',
        summary: 'First through the asteroid belt and first to Jupiter',
        payload: [
          {
            type: 'description',
            text: 'NASA approved a two-spacecraft Pioneer project to explore Jupiter in February 1969, managed by Ames Research Center, and TRW built the spacecraft. Pioneer 10 left Earth at about 51,682 km/h, faster than any human-made object before it, and passed the orbit of the Moon in less than 11 hours. It entered the asteroid belt on 15 July 1972 and came out unharmed on 15 February 1973.'
          },
          {
            type: 'stats',
            label: 'Jupiter encounter',
            entries: [
              { label: 'Closest approach', value: '4 December 1973, 02:26 UT' },
              { label: 'Distance',         value: 'About 2.8 Jupiter radii (about 200,000 km)' },
              { label: 'Speed',            value: 'About 126,000 km/h' },
              { label: 'Images',           value: 'About 500, best resolution about 320 km' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Jupiter's intense radiation triggered false commands that cost the mission one of its objectives, and an image of Io was lost to the radiation. Pioneer 10 still photographed Callisto, Ganymede and Europa."
          }
        ]
      },
      {
        id: 'instruments',
        label: 'Instruments',
        summary: '11 instruments for Jupiter and the space between',
        payload: [
          {
            type: 'description',
            text: 'Pioneer 10 carried 11 instruments, and six of them ran continuously through the Jupiter encounter. Its plutonium-238 generators gave about 155 W at launch and about 140 W at Jupiter. As the power fell, instruments were turned off or shared the power in turns, and by August 2000 only the Geiger tube telescope was still returning data.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Helium vector magnetometer', secondary: "Jupiter's magnetic field (failed November 1975)" },
              { primary: 'Plasma analyser, charged particle instrument, cosmic ray telescope, Geiger tube telescope, trapped radiation detector', secondary: 'The solar wind and radiation levels' },
              { primary: 'Meteoroid detectors and an asteroid/meteoroid detector', secondary: 'Interplanetary dust' },
              { primary: 'Ultraviolet photometer', secondary: 'Hydrogen and helium in space and on Jupiter' },
              { primary: 'Imaging photopolarimeter', secondary: 'Images of Jupiter (turned off October 1993 to save power)' },
              { primary: 'Infrared radiometer', secondary: 'Temperatures (failed January 1974)' }
            ]
          }
        ]
      },
      {
        id: 'where-now',
        label: 'Where it is now',
        summary: 'Silent since 2003, heading for the tail of the heliosphere',
        payload: [
          {
            type: 'stats',
            label: 'Predicted for 10 October 2026 (JPL Horizons)',
            entries: [
              { label: 'Distance from Sun',   value: '21.24 billion km (142.0 AU)' },
              { label: 'Distance from Earth', value: '21.18 billion km (141.5 AU)' },
              { label: 'Light time from Earth', value: '19 h 37 min one way' },
              { label: 'Speed from the Sun',  value: '11.8 km/s' }
            ]
          },
          {
            type: 'description',
            text: 'Pioneer 10 has been silent since 23 January 2003, when its generators no longer gave enough power to run the radio transmitter. That last signal took 11 hours and 20 minutes to reach Earth. The position above is a prediction from the JPL trajectory model. The probe is heading downstream towards the tail of the heliosphere, the opposite way to the Voyagers and Pioneer 11.'
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Pioneer 10 was the most distant human-made object until 17 February 1998, when Voyager 1 passed it at 69.4 AU.'
          }
        ]
      },
      {
        id: 'discoveries',
        label: 'Firsts and discoveries',
        summary: "The asteroid belt, Jupiter's magnetic tail and the Pioneer plaque",
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'First through the asteroid belt', secondary: 'In from 15 July 1972 to 15 February 1973, with fewer asteroid hits than expected' },
              { primary: 'First Jupiter flyby',             secondary: 'About 500 images including the Great Red Spot, and the first close-up images of Ganymede' },
              { primary: "Jupiter's magnetic tail",         secondary: "Crossing Saturn's orbit in February 1976, it found Jupiter's magnetic tail, almost 800 million km long, stretching across the whole distance between the two planets" },
              { primary: 'All-nuclear power',               secondary: 'The first spacecraft to use all-nuclear electrical power' },
              { primary: 'Beyond Neptune',                  secondary: 'On 13 June 1983 it crossed the orbit of Neptune, then the outermost planet, the first human-made object to go beyond it' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The Pioneer plaque: Pioneer 10 carries an aluminium plaque with drawings of a man and a woman, the solar system, and the Sun\'s position relative to 14 pulsars, so that anyone who finds it could work out where the Sun was when it launched.'
          }
        ]
      },
      {
        id: 'timeline',
        label: 'Timeline',
        summary: 'From launch in 1972 to the last signal in 2003',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: '2 March 1972',     secondary: 'Launch from Cape Canaveral on an Atlas-Centaur (3 March, 01:49 UTC)' },
              { primary: '15 July 1972',     secondary: 'The first spacecraft to enter the asteroid belt' },
              { primary: '15 February 1973', secondary: 'Leaves the asteroid belt' },
              { primary: '4 December 1973',  secondary: 'Closest approach to Jupiter' },
              { primary: 'February 1976',    secondary: 'Crosses the orbit of Saturn' },
              { primary: '13 June 1983',     secondary: 'Crosses the orbit of Neptune' },
              { primary: '31 March 1997',    secondary: 'Routine contact ends for budget reasons' },
              { primary: '17 February 1998', secondary: 'Overtaken by Voyager 1' },
              { primary: '27 April 2002',    secondary: 'Last telemetry data' },
              { primary: '23 January 2003',  secondary: 'Last signal received, from 12.23 billion km' },
              { primary: '4 March 2006',     secondary: 'A final attempt at contact fails' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where to dig deeper',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Pioneer 10', url: 'https://science.nasa.gov/mission/pioneer-10/' },
              { label: 'NSSDCA: Pioneer 10', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=1972-012A' },
              { label: 'NASA History: 50 years ago, Pioneer 10 launches', url: 'https://www.nasa.gov/history/50-years-ago-pioneer-10-launches-to-explore-jupiter/' },
              HORIZONS
            ]
          }
        ]
      }
    ]
  }
};

export const SMALL_BODY_TREES = {
  ...EARTH_ORBIT,
  ...MAIN_BELT,
  ...NEAR_EARTH,
  ...COMETS,
  ...INTERSTELLAR,
  ...OUTER_DWARFS,
  ...PROBES,
};
